import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../services/api';

/* =============================================================
   Lumix Óticas — OSList Clean
   UI/UX: Google-like, tela grudada, DPI pequeno, sem sombra pesada.
   Imagens: privadas por token via /ordens-servico/anexo-imagem.
============================================================= */

const STATUS_OPTIONS = [
  'Pedido da lente',
  'Pedido realizado',
  'No laboratório',
  'Pronto na loja',
  'Entregue'
];

const STATUS_META = {
  'Pedido da lente': {
    label: 'Pedir lente',
    dot: '#f97316',
    bg: '#fff7ed',
    text: '#9a3412',
    border: '#fed7aa'
  },
  'Pedido realizado': {
    label: 'Pedido feito',
    dot: '#1a73e8',
    bg: '#eff6ff',
    text: '#1d4ed8',
    border: '#bfdbfe'
  },
  'No laboratório': {
    label: 'No laboratório',
    dot: '#9333ea',
    bg: '#faf5ff',
    text: '#7e22ce',
    border: '#e9d5ff'
  },
  'Pronto na loja': {
    label: 'Pronto na loja',
    dot: '#16a34a',
    bg: '#f0fdf4',
    text: '#15803d',
    border: '#bbf7d0'
  },
  'Entregue': {
    label: 'Entregue',
    dot: '#64748b',
    bg: '#f8fafc',
    text: '#475569',
    border: '#e2e8f0'
  }
};

const Icons = {
  Search: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  ArrowLeft: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  ),
  Camera: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  ),
  Close: () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Save: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </svg>
  ),
  Plus: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  Trash: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  ),
  Refresh: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21.5 2v6h-6" />
      <path d="M21.34 15.57a10 10 0 1 1-.59-9.21L21.5 8" />
    </svg>
  )
};

/* =============================================================
   Helpers
============================================================= */

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(Number(value || 0));

const formatDateLocal = (dateString) => {
  if (!dateString) return '—';

  try {
    const datePart = String(dateString).split('T')[0];
    const [year, month, day] = datePart.split('-');

    if (!year || !month || !day) return String(dateString);

    return `${day}/${month}/${year}`;
  } catch {
    return String(dateString);
  }
};

const toInputDate = (dateString) => {
  if (!dateString) return '';

  try {
    return String(dateString).split('T')[0];
  } catch {
    return '';
  }
};

const getDateISOForFilter = (dateString) => {
  if (!dateString) return '';

  try {
    const date = new Date(dateString);

    if (Number.isNaN(date.getTime())) return '';

    return date.toISOString().split('T')[0];
  } catch {
    return '';
  }
};

const safeRenderName = (objOrStr) => {
  if (!objOrStr) return '';
  if (typeof objOrStr === 'string') return objOrStr;
  if (typeof objOrStr === 'object') return objOrStr.nome || objOrStr.nomeCompleto || '';
  return '';
};

const safeParseUser = () => {
  try {
    return JSON.parse(localStorage.getItem('vizumUser')) || {};
  } catch {
    return {};
  }
};

const normalizarId = (value) => {
  if (!value) return '';
  if (typeof value === 'object') return String(value._id || value.id || '');
  return String(value);
};

const normalizarTextoBusca = (valor = '') =>
  String(valor || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim();

const mesmoId = (a, b) => {
  if (!a || !b) return false;
  return String(a) === String(b);
};

const getEmpresaIdFromUser = (user = {}) => {
  const raw =
    user?.empresaId?._id ||
    user?.empresaId ||
    user?.empresa?._id ||
    user?.empresa?.id ||
    '';

  return raw ? String(raw) : '';
};


const resolverArrayApi = (data, fallbackKey = '') => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;
  if (fallbackKey && Array.isArray(data?.[fallbackKey])) return data[fallbackKey];
  if (Array.isArray(data?.empresas)) return data.empresas;
  if (Array.isArray(data?.ordens)) return data.ordens;
  if (Array.isArray(data?.ordensServico)) return data.ordensServico;
  return [];
};

const getEmpresaCodigo = (empresa = {}) =>
  String(empresa.codigoLoja || empresa.loja || empresa._id || empresa.id || '').trim();

const getEmpresaLabel = (empresa = {}) => {
  if (!empresa) return '';
  const nome = empresa.nome || empresa.razaoSocial || 'Empresa';
  const unidade = empresa.unidade && empresa.unidade !== 'Matriz' ? ` · ${empresa.unidade}` : '';
  return `${nome}${unidade}`;
};

const getEmpresaIdOS = (os = {}) =>
  normalizarId(
    os.empresaId ||
      os.empresa?._id ||
      os.empresa?.id ||
      os.empresa ||
      ''
  );

const encontrarEmpresaDaOS = (os = {}, empresas = []) => {
  if (!empresas.length) return null;

  const osEmpresaId = getEmpresaIdOS(os);
  const loja = String(os.loja || '').trim();

  if (osEmpresaId) {
    const porId = empresas.find((empresa) => {
      return mesmoId(empresa._id, osEmpresaId) || mesmoId(empresa.id, osEmpresaId);
    });

    if (porId) return porId;
  }

  if (!loja) return null;

  const busca = normalizarTextoBusca(loja);

  const porCodigo = empresas.find((empresa) => normalizarTextoBusca(empresa.codigoLoja) === busca);
  if (porCodigo) return porCodigo;

  const porUnidade = empresas.find((empresa) => normalizarTextoBusca(empresa.unidade) === busca);
  if (porUnidade) return porUnidade;

  const porNome = empresas.find((empresa) => normalizarTextoBusca(empresa.nome) === busca);
  if (porNome) return porNome;

  return (
    empresas.find((empresa) => {
      const alvo = [
        empresa.codigoLoja,
        empresa.unidade,
        empresa.nome,
        empresa.razaoSocial
      ]
        .map(normalizarTextoBusca)
        .filter(Boolean)
        .join(' ');

      return alvo.includes(busca) || busca.includes(alvo);
    }) || null
  );
};

const getOsItems = (os = {}) => {
  if (Array.isArray(os.itens) && os.itens.length) return os.itens;

  if (os.produto || os.receita || os.paciente) {
    return [
      {
        paciente: os.paciente || os.cliente || {},
        produto: os.produto || {},
        receita: os.receita || { od: {}, oe: {} }
      }
    ];
  }

  return [];
};

const getPrimeiroItem = (os = {}) => getOsItems(os)[0] || {};

const getPacientePrincipal = (os = {}) => {
  const primeiroItem = getPrimeiroItem(os);
  return safeRenderName(primeiroItem.paciente) || safeRenderName(os.paciente) || safeRenderName(os.cliente);
};

const getLentePrincipal = (os = {}) => {
  const item = getPrimeiroItem(os);
  return item.produto?.lente || os.produto?.lente || '';
};

const getArmacaoPrincipal = (os = {}) => {
  const item = getPrimeiroItem(os);
  return item.produto?.armacao || os.produto?.armacao || '';
};

const getNumeroCV = (os = {}) => {
  const item = getPrimeiroItem(os);
  return item.produto?.numeroCV || item.produto?.cv || item.produto?.codigoCV || os.produto?.numeroCV || '';
};

const getAnexos = (os = {}) => {
  return Array.isArray(os.anexos) ? os.anexos.filter(Boolean) : [];
};

const getPrimeiraFoto = (os = {}) => {
  const direto =
    os.primeiraFoto ||
    os.primeiroAnexo ||
    os.fotoPrincipal ||
    os.thumbnailUrl ||
    os.thumbUrl ||
    os.anexoPrincipal ||
    os?.preview?.foto ||
    '';

  if (typeof direto === 'string' && direto.trim()) return direto;

  if (direto && typeof direto === 'object') {
    return direto.url || direto.path || direto.caminho || '';
  }

  const primeiroAnexo = getAnexos(os)[0];

  if (typeof primeiroAnexo === 'string') return primeiroAnexo;

  if (primeiroAnexo && typeof primeiroAnexo === 'object') {
    return primeiroAnexo.url || primeiroAnexo.path || primeiroAnexo.caminho || '';
  }

  return '';
};

const parseMoneyValue = (value) => {
  if (typeof value === 'number') return value;

  const cleaned = String(value || '')
    .replace(/[^0-9,.]/g, '')
    .replace(',', '.');

  const number = Number(cleaned);
  return Number.isFinite(number) ? number : 0;
};

const normalizarNumeroEnvio = (phone = '') => {
  const clean = String(phone || '').replace(/\D/g, '');

  if (!clean) return '';
  if (clean.startsWith('55')) return clean;

  return `55${clean}`;
};

const criarMensagemProntoNaLoja = (os = {}, empresa = {}) => {
  const primeiroNome = (safeRenderName(os.cliente) || 'Cliente').split(' ')[0];
  const nomeEmpresa = getEmpresaLabel(empresa || {}) || os.loja || 'nossa ótica';
  const telefone = empresa?.whatsapp || empresa?.telefone || '';
  const endereco = empresa?.endereco || '';

  return `Olá, ${primeiroNome}! 😊\n\nTemos uma ótima notícia: seus óculos já estão *prontos* e aguardando retirada. 👓✨\n\n📍 *${nomeEmpresa}*${endereco ? `\n${endereco}` : ''}${telefone ? `\n📲 Contato: ${telefone}` : ''}\n\nPode passar aqui quando ficar melhor para você. Será um prazer te atender!`;
};

const calcularTotalPago = (os = {}) => {
  return Array.isArray(os.pagamentos)
    ? os.pagamentos.reduce((acc, p) => acc + (Number(p.valor) || 0), 0)
    : 0;
};

const calcularRestante = (os = {}) => {
  const total = Number(os.valorTotal || 0);
  const pago = calcularTotalPago(os);
  const restante = total - pago;

  return restante > 0.009 ? Number(restante.toFixed(2)) : 0;
};

const normalizarTexto = (value = '') =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const maskCep = (value = '') => {
  const clean = String(value || '').replace(/\D/g, '').slice(0, 8);

  if (clean.length <= 5) return clean;

  return `${clean.slice(0, 5)}-${clean.slice(5)}`;
};

const initials = (name = '') => {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) return 'OS';

  return parts
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();
};

/* =============================================================
   Imagem protegida
============================================================= */

function ProtectedImage({ src, alt = '', className = '', style = {}, fallbackText = 'OS', onClick }) {
  const [objectUrl, setObjectUrl] = useState('');
  const [erro, setErro] = useState(false);

  useEffect(() => {
    let active = true;
    let createdUrl = '';

    async function loadImage() {
      try {
        setErro(false);
        setObjectUrl('');

        if (!src) return;

        if (
          src.startsWith('data:') ||
          src.startsWith('blob:') ||
          src.startsWith('http://') ||
          src.startsWith('https://')
        ) {
          if (active) setObjectUrl(src);
          return;
        }

        const response = await api.get('/ordens-servico/anexo-imagem', {
          params: {
            path: src
          },
          responseType: 'blob'
        });

        createdUrl = URL.createObjectURL(response.data);

        if (active) {
          setObjectUrl(createdUrl);
        }
      } catch (error) {
        console.error('[ProtectedImage] Erro ao carregar imagem:', error);

        if (active) {
          setErro(true);
        }
      }
    }

    loadImage();

    return () => {
      active = false;

      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [src]);

  if (!src || erro || !objectUrl) {
    return (
      <div className={`protected-image-fallback ${className}`} style={style} onClick={onClick}>
        <span>{erro ? '!' : fallbackText}</span>
      </div>
    );
  }

  return (
    <img
      src={objectUrl}
      alt={alt}
      className={className}
      style={style}
      onClick={onClick}
      loading="lazy"
    />
  );
}

/* =============================================================
   UI menores
============================================================= */

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || STATUS_META['Entregue'];

  return (
    <span
      className="status-badge"
      style={{
        '--status-bg': meta.bg,
        '--status-text': meta.text,
        '--status-border': meta.border,
        '--status-dot': meta.dot
      }}
    >
      <i />
      {status || 'Sem status'}
    </span>
  );
}

function StatCell({ label, value, active, onClick, status }) {
  const meta = STATUS_META[status] || {
    dot: '#1a73e8',
    bg: '#f8fafc',
    text: '#334155',
    border: '#e2e8f0'
  };

  return (
    <button
      type="button"
      className={`stat-cell ${active ? 'active' : ''}`}
      onClick={onClick}
      style={{
        '--cell-dot': meta.dot,
        '--cell-bg': meta.bg,
        '--cell-text': meta.text,
        '--cell-border': meta.border
      }}
    >
      <span className="stat-cell-dot" />
      <span className="stat-cell-text">{label}</span>
      <strong>{value}</strong>
    </button>
  );
}

function FieldLine({ label, value }) {
  return (
    <div className="field-line">
      <span>{label}</span>
      <strong>{value || '—'}</strong>
    </div>
  );
}

/* =============================================================
   Componente principal
============================================================= */

export default function OSList({ onBack }) {
  const userLogado = useMemo(() => safeParseUser(), []);
  const empresaIdAtual = useMemo(() => getEmpresaIdFromUser(userLogado), [userLogado]);

  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
  const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];

  const [list, setList] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [totalResultados, setTotalResultados] = useState(0);
  const [temMaisResultados, setTemMaisResultados] = useState(false);
  const [carregandoMais, setCarregandoMais] = useState(false);
  const [filtrosAplicados, setFiltrosAplicados] = useState(null);

  const [loading, setLoading] = useState(true);
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [loadingDetalhe, setLoadingDetalhe] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [osDetalhada, setOsDetalhada] = useState(null);

  const [filters, setFilters] = useState({
    search: '',
    loja: '',
    status: '',
    dataInicio: startOfMonth,
    dataFim: endOfMonth
  });

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setFiltrosAplicados({ ...filters });
      setPaginaAtual(1);
      setSelectedId(null);
    }, filters.search ? 420 : 0);

    return () => window.clearTimeout(timer);
  }, [filters]);

  const fetchEmpresas = useCallback(async () => {
    setLoadingEmpresas(true);

    try {
      /*
        Mantido igual ao componente OSReports, que já funciona:
        não enviamos empresaId manual no params porque o backend já resolve
        permissão e escopo pelo token do usuário autenticado.
      */
      const response = await api.get('/empresas', {
        params: {
          comResumo: true
        }
      });

      setEmpresas(resolverArrayApi(response.data, 'empresas').filter((e) => e.ativo !== false));
    } catch (error) {
      console.warn('[OSList] Erro ao carregar empresas:', error);
      setEmpresas([]);
    } finally {
      setLoadingEmpresas(false);
    }
  }, []);

  const hidratarPrimeirasFotos = useCallback(async (ordens = []) => {
    const semFoto = ordens
      .filter((ordem) => ordem?._id && !getPrimeiraFoto(ordem))
      .slice(0, 10);

    if (!semFoto.length) return;

    try {
      const detalhes = await Promise.allSettled(
        semFoto.map((ordem) => api.get(`/ordens-servico/${ordem._id}`))
      );

      const fotosPorId = {};

      detalhes.forEach((resultado, index) => {
        if (resultado.status !== 'fulfilled') return;

        const detalhe = resultado.value?.data || {};
        const foto = getPrimeiraFoto(detalhe);
        const ordemId = semFoto[index]?._id;

        if (ordemId && foto) {
          fotosPorId[ordemId] = foto;
        }
      });

      if (!Object.keys(fotosPorId).length) return;

      setList((prev) =>
        prev.map((ordem) => {
          const foto = fotosPorId[ordem._id];

          if (!foto) return ordem;

          return {
            ...ordem,
            primeiraFoto: foto
          };
        })
      );
    } catch (error) {
      console.warn('[OSList] Não foi possível hidratar primeiras fotos:', error);
    }
  }, []);

  const fetchList = useCallback(async ({ pagina = 1, anexar = false } = {}) => {
    if (!filtrosAplicados) return;

    if (anexar) setCarregandoMais(true);
    else setLoading(true);

    try {
      const limite = 10;
      const params = {
        page: pagina,
        pagina,
        limit: limite,
        limite,
        porPagina: limite,
        sort: '-dataCompra',
        ordenarPor: 'dataCompra',
        ordem: 'desc'
      };

      if (filtrosAplicados.search?.trim()) params.busca = filtrosAplicados.search.trim();
      if (filtrosAplicados.dataInicio) params.dataInicio = filtrosAplicados.dataInicio;
      if (filtrosAplicados.dataFim) params.dataFim = filtrosAplicados.dataFim;
      if (filtrosAplicados.status) params.status = filtrosAplicados.status;
      if (filtrosAplicados.loja) params.empresaId = filtrosAplicados.loja;

      const response = await api.get('/ordens-servico', { params });
      const ordensRecebidas = resolverArrayApi(response.data, 'ordens');

      const respostaEhArray = Array.isArray(response.data);
      const apiIgnorouPaginacao = respostaEhArray && ordensRecebidas.length > limite;
      const inicioLocal = apiIgnorouPaginacao ? (pagina - 1) * limite : 0;
      const lote = ordensRecebidas.slice(inicioLocal, inicioLocal + limite);

      const meta = response.data?.pagination || response.data?.paginacao || response.data?.meta || response.data || {};
      const totalInformado = Number(
        meta.total ??
        meta.totalItems ??
        meta.totalRegistros ??
        meta.count ??
        response.data?.total
      );

      const total = Number.isFinite(totalInformado) && totalInformado >= 0
        ? totalInformado
        : (apiIgnorouPaginacao ? ordensRecebidas.length : ((pagina - 1) * limite) + lote.length + (lote.length === limite ? 1 : 0));

      const totalPaginas = Number(meta.totalPages ?? meta.totalPaginas ?? meta.pages) || Math.max(1, Math.ceil(total / limite));
      const temProximaInformada = meta.hasNextPage ?? meta.temProximaPagina ?? meta.hasNext;
      const temMais = typeof temProximaInformada === 'boolean'
        ? temProximaInformada
        : pagina < totalPaginas || (!Number.isFinite(totalInformado) && lote.length === limite);

      setList((prev) => {
        if (!anexar) return lote;

        const mapa = new Map(prev.map((ordem) => [ordem._id, ordem]));
        lote.forEach((ordem) => mapa.set(ordem._id, ordem));
        return Array.from(mapa.values());
      });

      setPaginaAtual(pagina);
      setTotalResultados(total);
      setTemMaisResultados(temMais);
      hidratarPrimeirasFotos(lote);
    } catch (error) {
      console.error('[OSList] Erro ao carregar OS paginadas:', error);
      if (!anexar) setList([]);
      setTemMaisResultados(false);
    } finally {
      setLoading(false);
      setCarregandoMais(false);
    }
  }, [filtrosAplicados, hidratarPrimeirasFotos]);

  const fetchDetalhe = useCallback(async (id) => {
    if (!id) return;

    setLoadingDetalhe(true);

    try {
      const response = await api.get(`/ordens-servico/${id}`);
      const detalhe = response.data;
      const foto = getPrimeiraFoto(detalhe);

      setOsDetalhada(detalhe);

      if (foto) {
        setList((prev) =>
          prev.map((ordem) =>
            ordem._id === id
              ? {
                  ...ordem,
                  primeiraFoto: foto
                }
              : ordem
          )
        );
      }
    } catch (error) {
      console.error('[OSList] Erro ao carregar detalhe:', error);
      setOsDetalhada(null);
    } finally {
      setLoadingDetalhe(false);
    }
  }, []);

  useEffect(() => {
    fetchEmpresas();
  }, [fetchEmpresas]);

  useEffect(() => {
    if (!filtrosAplicados) return;
    fetchList({ pagina: 1, anexar: false });
  }, [filtrosAplicados, fetchList]);

  useEffect(() => {
    if (selectedId) {
      fetchDetalhe(selectedId);
    } else {
      setOsDetalhada(null);
    }
  }, [selectedId, fetchDetalhe]);

  const listaEmpresasFiltro = useMemo(() => {
    const fallbackLojas = Array.from(
      new Set(
        list
          .map((item) => String(item.loja || '').trim())
          .filter(Boolean)
      )
    );

    if (empresas.length) {
      return empresas
        .map((empresa) => ({
          id: normalizarId(empresa._id || empresa.id),
          codigo: getEmpresaCodigo(empresa),
          label: getEmpresaLabel(empresa)
        }))
        .filter((empresa) => empresa.id || empresa.codigo)
        .sort((a, b) => a.label.localeCompare(b.label));
    }

    return fallbackLojas.map((loja) => ({
      id: loja,
      codigo: loja,
      label: loja
    }));
  }, [empresas, list]);

  const basePorPeriodoELoja = useMemo(() => list, [list]);

  const counts = useMemo(() => {
    return {
      total: totalResultados || list.length,
      lente: list.filter((os) => os.status === 'Pedido da lente').length,
      pedido: list.filter((os) => os.status === 'Pedido realizado').length,
      lab: list.filter((os) => os.status === 'No laboratório').length,
      pronto: list.filter((os) => os.status === 'Pronto na loja').length,
      entregue: list.filter((os) => os.status === 'Entregue').length,
      financeiro: list.reduce((acc, os) => acc + (Number(os.valorTotal) || 0), 0)
    };
  }, [list, totalResultados]);

  const filteredList = useMemo(() => list, [list]);

  const handleClearFilters = () => {
    setFilters({
      search: '',
      loja: '',
      status: '',
      dataInicio: startOfMonth,
      dataFim: endOfMonth
    });
  };

  const handleSelectOS = (id) => {
    setSelectedId((prev) => (prev === id ? null : id));
  };

  return (
    <div className="lumix-os-page">
      <GlobalStyles />

      <header className="lumix-top">
        <div className="lumix-top-left">
          {onBack ? (
            <button type="button" className="plain-btn" onClick={onBack}>
              <Icons.ArrowLeft />
              Voltar
            </button>
          ) : null}

          <div>
            <span className="kicker">Lumix Óticas</span>
            <h1>Ordens de serviço</h1>
            <p>Produção, fotos, status e acompanhamento por unidade.</p>
          </div>
        </div>

        <div className="top-summary">
          <div>
            <span>Total</span>
            <strong>{counts.total}</strong>
          </div>
          <div>
            <span>Vendas</span>
            <strong>{formatCurrency(counts.financeiro)}</strong>
          </div>
        </div>
      </header>

      <section className="stats-strip">
        <StatCell
          label="Todas"
          value={counts.total}
          active={!filters.status}
          onClick={() => setFilters((prev) => ({ ...prev, status: '' }))}
        />

        <StatCell
          label="Pedir lente"
          value={counts.lente}
          status="Pedido da lente"
          active={filters.status === 'Pedido da lente'}
          onClick={() =>
            setFilters((prev) => ({
              ...prev,
              status: prev.status === 'Pedido da lente' ? '' : 'Pedido da lente'
            }))
          }
        />

        <StatCell
          label="Pedido feito"
          value={counts.pedido}
          status="Pedido realizado"
          active={filters.status === 'Pedido realizado'}
          onClick={() =>
            setFilters((prev) => ({
              ...prev,
              status: prev.status === 'Pedido realizado' ? '' : 'Pedido realizado'
            }))
          }
        />

        <StatCell
          label="Laboratório"
          value={counts.lab}
          status="No laboratório"
          active={filters.status === 'No laboratório'}
          onClick={() =>
            setFilters((prev) => ({
              ...prev,
              status: prev.status === 'No laboratório' ? '' : 'No laboratório'
            }))
          }
        />

        <StatCell
          label="Pronto"
          value={counts.pronto}
          status="Pronto na loja"
          active={filters.status === 'Pronto na loja'}
          onClick={() =>
            setFilters((prev) => ({
              ...prev,
              status: prev.status === 'Pronto na loja' ? '' : 'Pronto na loja'
            }))
          }
        />

        <StatCell
          label="Entregue"
          value={counts.entregue}
          status="Entregue"
          active={filters.status === 'Entregue'}
          onClick={() =>
            setFilters((prev) => ({
              ...prev,
              status: prev.status === 'Entregue' ? '' : 'Entregue'
            }))
          }
        />
      </section>

      <section className="filters-line">
        <div className="search-box">
          <Icons.Search />
          <input
            value={filters.search}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
            placeholder="Buscar cliente, OS, CV, lente, loja..."
          />
        </div>

        <select
          value={filters.loja}
          onChange={(e) => setFilters((prev) => ({ ...prev, loja: e.target.value }))}
          disabled={loadingEmpresas}
        >
          <option value="">{loadingEmpresas ? 'Carregando...' : 'Todas as óticas'}</option>
          {listaEmpresasFiltro.map((empresa) => (
            <option key={empresa.id || empresa.codigo} value={empresa.id || empresa.codigo}>
              {empresa.label}
            </option>
          ))}
        </select>

        <input
          type="date"
          value={filters.dataInicio}
          onChange={(e) => setFilters((prev) => ({ ...prev, dataInicio: e.target.value }))}
        />

        <input
          type="date"
          value={filters.dataFim}
          onChange={(e) => setFilters((prev) => ({ ...prev, dataFim: e.target.value }))}
        />

        <button type="button" className="plain-icon-btn" onClick={handleClearFilters} title="Limpar filtros">
          <Icons.Close />
        </button>

        <button type="button" className="plain-icon-btn" onClick={() => fetchList({ pagina: 1, anexar: false })} title="Atualizar">
          <Icons.Refresh />
        </button>
      </section>

      <main className={`content-grid ${selectedId ? 'detail-open' : ''}`}>
        <section className="list-pane">
          {selectedId ? (
            <button
              type="button"
              className="list-restore-rail"
              onClick={() => setSelectedId(null)}
              title="Fechar detalhe e voltar para a lista"
            >
              <Icons.Close />
              <span>Fechar</span>
            </button>
          ) : (
            <>
          <div className="pane-head">
            <span>{totalResultados || filteredList.length} resultado(s)</span>
            <small>10 por carregamento</small>
          </div>

          {loading ? (
            <div className="empty-panel">
              <div className="thin-loader" />
              <span>Carregando ordens...</span>
            </div>
          ) : filteredList.length === 0 ? (
            <div className="empty-panel">
              <span>Nenhuma OS encontrada.</span>
              <button type="button" onClick={handleClearFilters}>Limpar filtros</button>
            </div>
          ) : (
            <>
              <div className="os-list">
                {filteredList.map((os) => (
                  <OSCompactRow
                    key={os._id}
                    os={os}
                    empresa={encontrarEmpresaDaOS(os, empresas)}
                    active={selectedId === os._id}
                    onClick={() => handleSelectOS(os._id)}
                  />
                ))}
              </div>

              {temMaisResultados ? (
                <div className="load-more-wrap">
                  <button
                    type="button"
                    className="load-more-btn"
                    disabled={carregandoMais}
                    onClick={() => fetchList({ pagina: paginaAtual + 1, anexar: true })}
                  >
                    {carregandoMais ? 'Carregando...' : 'Carregar mais 10'}
                  </button>
                </div>
              ) : null}
            </>
          )}
            </>
          )}
        </section>

        <section className="detail-pane">
          {loadingDetalhe ? (
            <DetailLoading />
          ) : osDetalhada ? (
            <OSInlineDetail
              os={osDetalhada}
              empresas={empresas}
              empresaIdAtual={empresaIdAtual}
              onClose={() => setSelectedId(null)}
              onReload={() => {
                fetchDetalhe(osDetalhada._id);
                fetchList({ pagina: 1, anexar: false });
              }}
            />
          ) : (
            <div className="detail-placeholder">
              <span>Selecione uma ordem</span>
              <p>O detalhe abre aqui, na própria tela, sem modal.</p>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

/* =============================================================
   Linha compacta
============================================================= */

function OSCompactRow({ os, empresa, active, onClick }) {
  const cliente = safeRenderName(os.cliente) || 'Cliente não informado';
  const paciente = getPacientePrincipal(os);
  const primeiraFoto = getPrimeiraFoto(os);
  const cv = getNumeroCV(os);
  const lente = getLentePrincipal(os);
  const armacao = getArmacaoPrincipal(os);
  const total = Number(os.valorTotal || 0);
  const pago = calcularTotalPago(os);
  const percent = total > 0 ? Math.min(100, Math.round((pago / total) * 100)) : 0;
  const restante = calcularRestante(os);

  return (
    <button type="button" className={`os-row ${active ? 'active' : ''}`} onClick={onClick}>
      <div className="row-photo">
        <ProtectedImage
          src={primeiraFoto}
          alt={cliente}
          fallbackText={initials(cliente)}
          className="row-photo-img"
        />
      </div>

      <div className="row-main">
        <div className="row-title-line">
          <strong>{cliente}</strong>
          <span>OS #{os.numeroOS || '—'}</span>
        </div>

        <div className="row-sub-line">
          <span>{paciente || 'Paciente não informado'}</span>
          {cv ? <i>CV {cv}</i> : null}
        </div>

        <div className="row-product-line">
          <span>{lente || 'Lente não informada'}</span>
          {armacao ? <em>{armacao}</em> : null}
        </div>

        <div className="row-bottom-line">
          <span>{empresa ? getEmpresaLabel(empresa) : os.loja || 'Sem loja'}</span>
          <span>Compra {formatDateLocal(os.dataCompra)}</span>
        </div>
      </div>

      <div className="row-side">
        <StatusBadge status={os.status} />

        <div className="row-money">
          <strong>{formatCurrency(total)}</strong>
          <span>{restante > 0 ? `${formatCurrency(restante)} aberto` : 'Quitado'}</span>
        </div>

        <div className="mini-paybar">
          <i style={{ width: `${percent}%` }} />
        </div>
      </div>
    </button>
  );
}

/* =============================================================
   Detalhe inline
============================================================= */

function DetailLoading() {
  return (
    <div className="detail-loading">
      <div className="thin-loader" />
      <span>Carregando detalhe da OS...</span>
    </div>
  );
}

function OSInlineDetail({ os, empresas, empresaIdAtual, onClose, onReload }) {
  const [tab, setTab] = useState('resumo');
  const [status, setStatus] = useState(os.status || 'Pedido da lente');
  const [obs, setObs] = useState('');
  const [obsRapida, setObsRapida] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fotoZoom, setFotoZoom] = useState(null);
  const [enviarAvisoRetirada, setEnviarAvisoRetirada] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [editForm, setEditForm] = useState(() => montarEditFormOS(os, empresas, empresaIdAtual));

  useEffect(() => {
    setStatus(os.status || 'Pedido da lente');
    setObs('');
    setObsRapida('');
    setUploadProgress(0);
    setEnviarAvisoRetirada(false);
    setBuscandoCep(false);
    setEditForm(montarEditFormOS(os, empresas, empresaIdAtual));
  }, [os, empresas, empresaIdAtual]);

  useEffect(() => {
    if (tab !== 'editar') return undefined;

    const cepLimpo = String(editForm.endereco?.cep || '').replace(/\D/g, '');

    if (cepLimpo.length !== 8) {
      setBuscandoCep(false);
      return undefined;
    }

    let cancelado = false;

    const timeout = window.setTimeout(async () => {
      setBuscandoCep(true);

      try {
        const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
        const data = await response.json();

        if (cancelado || data?.erro) return;

        setEditForm((prev) => ({
          ...prev,
          endereco: {
            ...prev.endereco,
            cep: maskCep(cepLimpo),
            logradouro: data.logradouro || prev.endereco.logradouro,
            bairro: data.bairro || prev.endereco.bairro,
            cidade: data.localidade || prev.endereco.cidade,
            uf: data.uf || prev.endereco.uf
          }
        }));
      } catch (error) {
        console.warn('[OSList] Erro ao buscar CEP:', error);
      } finally {
        if (!cancelado) {
          setBuscandoCep(false);
        }
      }
    }, 450);

    return () => {
      cancelado = true;
      window.clearTimeout(timeout);
    };
  }, [editForm.endereco?.cep, tab]);

  const empresa = encontrarEmpresaDaOS(os, empresas);
  const cliente = safeRenderName(os.cliente) || 'Cliente não informado';
  const paciente = getPacientePrincipal(os);
  const itens = getOsItems(os);
  const anexos = getAnexos(os);
  const primeiraFoto = getPrimeiraFoto(os);
  const total = Number(os.valorTotal || 0);
  const pago = calcularTotalPago(os);
  const restante = calcularRestante(os);
  const statusVaiParaPronto = status === 'Pronto na loja' && os.status !== 'Pronto na loja';
  const podeEnviarAvisoRetirada = statusVaiParaPronto && Boolean(os.whatsapp);

  const handleStatusChange = (novoStatus) => {
    setStatus(novoStatus);

    if (novoStatus === 'Pronto na loja' && os.status !== 'Pronto na loja' && os.whatsapp) {
      setEnviarAvisoRetirada(true);
      setObs((prev) => prev || 'Cliente avisado que os óculos estão prontos para retirada.');
      return;
    }

    setEnviarAvisoRetirada(false);
  };

  const enviarAvisoProntoNaLoja = async () => {
    const phone = normalizarNumeroEnvio(os.whatsapp);

    if (!phone) {
      throw new Error('WhatsApp do cliente não informado.');
    }

    const empresaCanalId = getEmpresaIdOS(os) || normalizarId(empresa?._id || empresa?.id) || empresaIdAtual || null;
    const message = criarMensagemProntoNaLoja(os, empresa || {});

    await api.post('/api/whatsapp/send', {
      empresaId: empresaCanalId,
      canalEmpresaId: empresaCanalId,
      loja: os.loja || getEmpresaCodigo(empresa || {}),
      tipoMensagem: 'aviso_pronto_loja',
      number: phone,
      message
    });
  };

  const handleSalvarStatus = async () => {
    if (statusVaiParaPronto && podeEnviarAvisoRetirada && enviarAvisoRetirada) {
      const confirmar = window.confirm('Enviar aviso de retirada para o cliente e marcar como Pronto na loja?');
      if (!confirmar) return;
    }

    setSaving(true);

    try {
      if (statusVaiParaPronto && podeEnviarAvisoRetirada && enviarAvisoRetirada) {
        await enviarAvisoProntoNaLoja();
      }

      const registro = obs || (
        statusVaiParaPronto && enviarAvisoRetirada
          ? '🤖 Aviso de retirada enviado. Status atualizado para Pronto na loja.'
          : `⚙️ Status atualizado para ${status}.`
      );

      await api.put(`/ordens-servico/${os._id}`, {
        status,
        registroTexto: registro
      });

      onReload?.();
    } catch (error) {
      console.error(error);
      alert(error.message || 'Erro ao salvar status.');
    } finally {
      setSaving(false);
    }
  };

  const handleAdicionarObservacao = async () => {
    const texto = obsRapida.trim();

    if (!texto) {
      alert('Digite uma observação antes de salvar.');
      return;
    }

    setSaving(true);

    try {
      await api.put(`/ordens-servico/${os._id}`, {
        registroTexto: texto
      });

      setObsRapida('');
      onReload?.();
    } catch (error) {
      console.error(error);
      alert('Erro ao salvar observação.');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadFotos = async (event) => {
    const files = Array.from(event.target.files || []);

    if (!files.length) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const urls = [];

      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        const formData = new FormData();

        formData.append('imagem', file);
        formData.append('empresaId', getEmpresaIdOS(os) || empresaIdAtual || '');

        const response = await api.post('/ordens-servico/upload-anexo', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const currentFileBase = (i / files.length) * 100;
            const currentFilePercent = progressEvent.total
              ? (progressEvent.loaded / progressEvent.total) * (100 / files.length)
              : 0;

            setUploadProgress(Math.min(98, Math.round(currentFileBase + currentFilePercent)));
          }
        });

        const url = response.data?.url || response.data?.imageUrl;

        if (url) urls.push(url);
      }

      if (urls.length) {
        await api.patch(`/ordens-servico/${os._id}/adicionar-anexos`, {
          novosAnexos: urls
        });
      }

      setUploadProgress(100);
      onReload?.();
    } catch (error) {
      console.error(error);
      alert('Erro ao enviar foto.');
    } finally {
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
      }, 300);

      event.target.value = null;
    }
  };

  const handleRemoverFoto = async (index) => {
    if (!window.confirm('Remover esta foto da OS?')) return;

    try {
      const novosAnexos = anexos.filter((_, i) => i !== index);

      await api.put(`/ordens-servico/${os._id}`, {
        anexos: novosAnexos,
        registroTexto: '📸 Foto removida da OS.'
      });

      onReload?.();
    } catch (error) {
      console.error(error);
      alert('Erro ao remover foto.');
    }
  };

  const updateEditField = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateEditCliente = (field, value) => {
    setEditForm((prev) => ({
      ...prev,
      cliente: {
        ...prev.cliente,
        [field]: value
      }
    }));
  };

  const updateEditEndereco = (field, value) => {
    const finalValue = field === 'cep' ? maskCep(value) : value;

    setEditForm((prev) => ({
      ...prev,
      endereco: {
        ...prev.endereco,
        [field]: finalValue
      }
    }));
  };

  const updateEditItem = (index, path, value) => {
    setEditForm((prev) => {
      const itensAtualizados = prev.itens.map((item, itemIndex) => {
        if (itemIndex !== index) return item;

        const copy = JSON.parse(JSON.stringify(item));
        const keys = path.split('.');
        let ref = copy;

        for (let i = 0; i < keys.length - 1; i += 1) {
          if (!ref[keys[i]]) ref[keys[i]] = {};
          ref = ref[keys[i]];
        }

        ref[keys[keys.length - 1]] = value;
        return copy;
      });

      return {
        ...prev,
        itens: itensAtualizados
      };
    });
  };

  const addEditItem = () => {
    setEditForm((prev) => ({
      ...prev,
      itens: [
        ...prev.itens,
        criarItemEditavel()
      ]
    }));
  };

  const removeEditItem = (index) => {
    if (editForm.itens.length <= 1) {
      alert('A OS precisa ter pelo menos um óculos.');
      return;
    }

    setEditForm((prev) => ({
      ...prev,
      itens: prev.itens.filter((_, itemIndex) => itemIndex !== index)
    }));
  };

  const updateEditPagamento = (index, field, value) => {
    setEditForm((prev) => ({
      ...prev,
      pagamentos: prev.pagamentos.map((pagamento, pagamentoIndex) => {
        if (pagamentoIndex !== index) return pagamento;
        return {
          ...pagamento,
          [field]: field === 'valor' || field === 'parcelas' ? value : value
        };
      })
    }));
  };

  const addEditPagamento = () => {
    setEditForm((prev) => ({
      ...prev,
      pagamentos: [
        ...prev.pagamentos,
        {
          metodo: 'PIX',
          valor: '',
          parcelas: 1,
          data: new Date().toISOString()
        }
      ]
    }));
  };

  const removeEditPagamento = (index) => {
    setEditForm((prev) => ({
      ...prev,
      pagamentos: prev.pagamentos.filter((_, pagamentoIndex) => pagamentoIndex !== index)
    }));
  };

  const handleEditEmpresaChange = (value) => {
    const empresaSelecionada = empresas.find((emp) => {
      return (
        mesmoId(emp._id, value) ||
        mesmoId(emp.id, value) ||
        getEmpresaCodigo(emp) === value
      );
    });

    setEditForm((prev) => ({
      ...prev,
      empresaId: empresaSelecionada?._id || empresaSelecionada?.id || value,
      loja: empresaSelecionada ? getEmpresaCodigo(empresaSelecionada) : value
    }));
  };

  const handleSalvarEdicao = async () => {
    if (!editForm.cliente.nome.trim()) {
      alert('Informe o nome do cliente.');
      return;
    }

    if (!editForm.vendedor.trim()) {
      alert('Informe o vendedor.');
      return;
    }

    const itensValidos = editForm.itens.filter((item) => {
      return item.paciente?.nome?.trim() && item.produto?.lente?.trim();
    });

    if (!itensValidos.length) {
      alert('Informe pelo menos um óculos com paciente e lente.');
      return;
    }

    setSaving(true);

    try {
      const payload = {
        empresaId: editForm.empresaId || getEmpresaIdOS(os) || empresaIdAtual || undefined,
        loja: editForm.loja || os.loja || '',
        vendedor: editForm.vendedor,
        whatsapp: editForm.whatsapp,
        dataCompra: editForm.dataCompra || undefined,
        dataEntrega: editForm.dataEntrega || null,
        valorTotal: parseMoneyValue(editForm.valorTotal),
        pagamentos: editForm.pagamentos.map((pagamento) => ({
          metodo: pagamento.metodo || 'PIX',
          valor: parseMoneyValue(pagamento.valor),
          parcelas: Number(pagamento.parcelas || 1) || 1,
          data: pagamento.data || new Date().toISOString()
        })),
        cliente: {
          nome: editForm.cliente.nome,
          cpf: editForm.cliente.cpf,
          documento: editForm.cliente.documento,
          email: editForm.cliente.email,
          dataNascimento: editForm.cliente.dataNascimento || null
        },
        endereco: editForm.endereco,
        itens: itensValidos.map((item) => ({
          paciente: {
            nome: item.paciente.nome
          },
          receita: {
            od: item.receita?.od || {},
            oe: item.receita?.oe || {},
            adicao: item.receita?.adicao || ''
          },
          produto: {
            lente: item.produto?.lente || '',
            armacao: item.produto?.armacao || '',
            numeroCV: item.produto?.numeroCV || '',
            medidasArmacao: item.produto?.medidasArmacao || {},
            origemArmacao: item.produto?.origemArmacao || 'Ótica',
            envioArmacao: item.produto?.envioArmacao || 'Vai no malote',
            formatoArmacao: item.produto?.formatoArmacao || '',
            ncm: item.produto?.ncm || '',
            cfop: item.produto?.cfop || ''
          },
          quantidade: Number(item.quantidade || 1) || 1,
          valorUnitario: parseMoneyValue(item.valorUnitario),
          valorTotal: parseMoneyValue(item.valorTotal)
        })),
        nfceUrl: editForm.nfceUrl || null,
        nfceXmlUrl: editForm.nfceXmlUrl || null,
        chaveNfe: editForm.chaveNfe || null,
        registroTexto: '📝 Dados da OS editados pelo painel.'
      };

      await api.put(`/ordens-servico/${os._id}`, payload);
      setTab('resumo');
      onReload?.();
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.error || 'Erro ao salvar edição.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="detail-shell">
      <div className="detail-top">
        <div className="detail-profile">
          <ProtectedImage
            src={primeiraFoto}
            alt={cliente}
            fallbackText={initials(cliente)}
            className="detail-avatar"
          />

          <div>
            <span className="detail-kicker">OS #{os.numeroOS || '—'}</span>
            <h2>{cliente}</h2>
            <p>{paciente || 'Paciente não informado'}</p>
          </div>
        </div>

        <button type="button" className="plain-icon-btn" onClick={onClose} title="Fechar detalhe">
          <Icons.Close />
        </button>
      </div>

      <div className="detail-meta-line">
        <StatusBadge status={os.status} />
        <span>{empresa ? getEmpresaLabel(empresa) : os.loja || 'Sem loja'}</span>
        <span>{formatDateLocal(os.dataCompra)}</span>
        {getNumeroCV(os) ? <span>CV {getNumeroCV(os)}</span> : null}
      </div>

      <div className="detail-tabs">
        <button type="button" className={tab === 'resumo' ? 'active' : ''} onClick={() => setTab('resumo')}>
          Resumo
        </button>
        <button type="button" className={tab === 'itens' ? 'active' : ''} onClick={() => setTab('itens')}>
          Itens
        </button>
        <button type="button" className={tab === 'fotos' ? 'active' : ''} onClick={() => setTab('fotos')}>
          Fotos {anexos.length ? `(${anexos.length})` : ''}
        </button>
        <button type="button" className={tab === 'status' ? 'active' : ''} onClick={() => setTab('status')}>
          Status / aviso
        </button>
        <button type="button" className={tab === 'editar' ? 'active' : ''} onClick={() => setTab('editar')}>
          Editar
        </button>
      </div>

      <div className="detail-body">
        {tab === 'resumo' ? (
          <div className="detail-section-grid">
            <div className="flat-panel">
              <h3>Venda</h3>
              <FieldLine label="Cliente" value={cliente} />
              <FieldLine label="Paciente" value={paciente} />
              <FieldLine label="WhatsApp" value={os.whatsapp} />
              <FieldLine label="Vendedor" value={os.vendedor} />
              <FieldLine label="Compra" value={formatDateLocal(os.dataCompra)} />
              <FieldLine label="Entrega" value={formatDateLocal(os.dataEntrega)} />
            </div>

            <div className="flat-panel">
              <h3>Financeiro</h3>
              <FieldLine label="Total" value={formatCurrency(total)} />
              <FieldLine label="Pago" value={formatCurrency(pago)} />
              <FieldLine label="Saldo" value={restante > 0 ? formatCurrency(restante) : 'Quitado'} />

              <div className="payment-list-clean">
                {Array.isArray(os.pagamentos) && os.pagamentos.length ? (
                  os.pagamentos.map((p, index) => (
                    <div key={p._id || p.id || index}>
                      <span>{p.metodo || 'Pagamento'}</span>
                      <strong>{formatCurrency(p.valor)}</strong>
                    </div>
                  ))
                ) : (
                  <small>Nenhum pagamento registrado.</small>
                )}
              </div>
            </div>

            <div className="flat-panel full observation-panel">
              <div className="panel-title-line">
                <h3>Observação rápida</h3>
                <span>Fica registrada no histórico da OS</span>
              </div>

              <div className="quick-note-row">
                <input
                  value={obsRapida}
                  onChange={(e) => setObsRapida(e.target.value)}
                  placeholder="Ex: cliente pediu urgência, ajuste no nariz, ligar antes da entrega..."
                />
                <button type="button" onClick={handleAdicionarObservacao} disabled={saving}>
                  {saving ? 'Salvando...' : 'Adicionar'}
                </button>
              </div>
            </div>

            <div className="flat-panel full">
              <h3>Observações / histórico</h3>

              <div className="history-clean">
                {Array.isArray(os.registros) && os.registros.length ? (
                  os.registros
                    .slice()
                    .reverse()
                    .slice(0, 10)
                    .map((registro, index) => (
                      <div key={registro._id || index}>
                        <span>{registro.createdAt ? new Date(registro.createdAt).toLocaleString('pt-BR') : '—'}</span>
                        <p>{registro.texto}</p>
                      </div>
                    ))
                ) : (
                  <small>Sem histórico.</small>
                )}
              </div>
            </div>
          </div>
        ) : null}

        {tab === 'itens' ? (
          <div className="items-list-clean">
            {itens.length ? (
              itens.map((item, index) => (
                <div key={index} className="item-clean-card">
                  <div className="item-clean-head">
                    <strong>Óculos {index + 1}</strong>
                    <span>{safeRenderName(item.paciente) || 'Paciente não informado'}</span>
                  </div>

                  <div className="item-clean-grid">
                    <FieldLine label="Lente" value={item.produto?.lente} />
                    <FieldLine label="Armação" value={item.produto?.armacao} />
                    <FieldLine label="CV" value={item.produto?.numeroCV || item.produto?.cv || item.produto?.codigoCV} />
                    <FieldLine label="Origem" value={item.produto?.origemArmacao} />
                    <FieldLine label="Envio" value={item.produto?.envioArmacao} />
                    <FieldLine label="Formato" value={item.produto?.formatoArmacao} />
                  </div>

                  <div className="rx-clean">
                    <table>
                      <thead>
                        <tr>
                          <th>Olho</th>
                          <th>ESF</th>
                          <th>CIL</th>
                          <th>Eixo</th>
                          <th>DNP</th>
                          <th>ALT</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>OD</td>
                          <td>{item.receita?.od?.esf || '—'}</td>
                          <td>{item.receita?.od?.cil || '—'}</td>
                          <td>{item.receita?.od?.eixo || '—'}</td>
                          <td>{item.receita?.od?.dnp || '—'}</td>
                          <td>{item.receita?.od?.aco || '—'}</td>
                        </tr>
                        <tr>
                          <td>OE</td>
                          <td>{item.receita?.oe?.esf || '—'}</td>
                          <td>{item.receita?.oe?.cil || '—'}</td>
                          <td>{item.receita?.oe?.eixo || '—'}</td>
                          <td>{item.receita?.oe?.dnp || '—'}</td>
                          <td>{item.receita?.oe?.aco || '—'}</td>
                        </tr>
                      </tbody>
                    </table>
                    <span>Adição: {item.receita?.adicao || '—'}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-panel">Nenhum item encontrado.</div>
            )}
          </div>
        ) : null}

        {tab === 'fotos' ? (
          <div className="photos-section">
            <div className="photo-toolbar">
              <label className="upload-clean-btn">
                <Icons.Camera />
                {uploading ? `Enviando ${uploadProgress}%` : 'Adicionar fotos'}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  disabled={uploading}
                  onChange={handleUploadFotos}
                />
              </label>

              {uploading ? (
                <div className="upload-progress-line">
                  <i style={{ width: `${uploadProgress}%` }} />
                </div>
              ) : null}
            </div>

            {anexos.length ? (
              <div className="photo-grid-clean">
                {anexos.map((foto, index) => (
                  <div key={`${foto}-${index}`} className="photo-clean-card">
                    <ProtectedImage
                      src={foto}
                      alt={`Foto ${index + 1}`}
                      className="photo-clean-img"
                      fallbackText={`${index + 1}`}
                      onClick={() => setFotoZoom(foto)}
                    />

                    <button
                      type="button"
                      className="photo-remove"
                      onClick={() => handleRemoverFoto(index)}
                      title="Remover foto"
                    >
                      <Icons.Trash />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-panel">Sem fotos anexadas.</div>
            )}
          </div>
        ) : null}

        {tab === 'status' ? (
          <div className="flat-panel status-panel-clean">
            <h3>Status e aviso ao cliente</h3>

            <div className="status-form-grid">
              <label>
                Status
                <select value={status} onChange={(e) => handleStatusChange(e.target.value)}>
                  {STATUS_OPTIONS.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </label>

              {statusVaiParaPronto ? (
                <div className="ready-notice">
                  <strong>Pronto na loja</strong>
                  <p>
                    {os.whatsapp
                      ? 'Ao salvar, você pode enviar o aviso de retirada pelo WhatsApp automaticamente.'
                      : 'Esta OS não tem WhatsApp cadastrado. O status será alterado sem envio de aviso.'}
                  </p>

                  {os.whatsapp ? (
                    <label className="checkbox-clean">
                      <input
                        type="checkbox"
                        checked={enviarAvisoRetirada}
                        onChange={(e) => setEnviarAvisoRetirada(e.target.checked)}
                      />
                      Enviar aviso de retirada para o cliente
                    </label>
                  ) : null}
                </div>
              ) : null}

              <label>
                Observação
                <input
                  value={obs}
                  onChange={(e) => setObs(e.target.value)}
                  placeholder="Ex: chegou do laboratório, cliente avisado..."
                />
              </label>
            </div>

            <button
              type="button"
              className="primary-clean-btn"
              onClick={handleSalvarStatus}
              disabled={saving}
            >
              <Icons.Save />
              {saving ? 'Salvando...' : 'Salvar status'}
            </button>
          </div>
        ) : null}

        {tab === 'editar' ? (
          <div className="edit-clean-layout">
            <div className="flat-panel full">
              <div className="panel-title-line">
                <h3>Editar OS</h3>
                <span>Dados principais, itens, pagamentos e informações fiscais manuais</span>
              </div>

              <div className="edit-grid-3">
                <label>
                  Loja / unidade
                  <select value={editForm.empresaId || editForm.loja} onChange={(e) => handleEditEmpresaChange(e.target.value)}>
                    <option value="">Selecione a empresa</option>
                    {empresas.map((emp) => (
                      <option key={emp._id || emp.id || emp.codigoLoja} value={emp._id || emp.id || emp.codigoLoja}>
                        {getEmpresaLabel(emp)}
                      </option>
                    ))}
                    {!empresas.length && editForm.loja ? <option value={editForm.loja}>{editForm.loja}</option> : null}
                  </select>
                </label>

                <label>
                  Vendedor
                  <input value={editForm.vendedor} onChange={(e) => updateEditField('vendedor', e.target.value)} />
                </label>

                <label>
                  WhatsApp
                  <input value={editForm.whatsapp} onChange={(e) => updateEditField('whatsapp', e.target.value)} />
                </label>

                <label>
                  Cliente
                  <input value={editForm.cliente.nome} onChange={(e) => updateEditCliente('nome', e.target.value)} />
                </label>

                <label>
                  CPF
                  <input value={editForm.cliente.cpf} onChange={(e) => updateEditCliente('cpf', e.target.value)} />
                </label>

                <label>
                  Nascimento
                  <input type="date" value={editForm.cliente.dataNascimento} onChange={(e) => updateEditCliente('dataNascimento', e.target.value)} />
                </label>

                <label>
                  Compra
                  <input type="date" value={editForm.dataCompra} onChange={(e) => updateEditField('dataCompra', e.target.value)} />
                </label>

                <label>
                  Entrega
                  <input type="date" value={editForm.dataEntrega} onChange={(e) => updateEditField('dataEntrega', e.target.value)} />
                </label>

                <label>
                  Valor total
                  <input value={editForm.valorTotal} onChange={(e) => updateEditField('valorTotal', e.target.value)} />
                </label>
              </div>
            </div>

            <div className="flat-panel full">
              <h3>Endereço</h3>

              <div className="edit-grid-3">
                <label>
                  <span className="label-with-status">
                    CEP
                    {buscandoCep ? <small>Buscando...</small> : null}
                  </span>
                  <input
                    value={editForm.endereco.cep}
                    onChange={(e) => updateEditEndereco('cep', e.target.value)}
                    placeholder="00000-000"
                    inputMode="numeric"
                  />
                </label>
                <label className="span-2">
                  Logradouro
                  <input value={editForm.endereco.logradouro} onChange={(e) => updateEditEndereco('logradouro', e.target.value)} />
                </label>
                <label>
                  Número
                  <input value={editForm.endereco.numero} onChange={(e) => updateEditEndereco('numero', e.target.value)} />
                </label>
                <label>
                  Bairro
                  <input value={editForm.endereco.bairro} onChange={(e) => updateEditEndereco('bairro', e.target.value)} />
                </label>
                <label>
                  Cidade
                  <input value={editForm.endereco.cidade} onChange={(e) => updateEditEndereco('cidade', e.target.value)} />
                </label>
                <label>
                  UF
                  <input value={editForm.endereco.uf} onChange={(e) => updateEditEndereco('uf', e.target.value)} />
                </label>
                <label className="span-2">
                  Complemento
                  <input value={editForm.endereco.complemento} onChange={(e) => updateEditEndereco('complemento', e.target.value)} />
                </label>
              </div>
            </div>

            <div className="flat-panel full">
              <div className="panel-title-line">
                <h3>Itens / óculos</h3>
                <button type="button" className="small-clean-btn" onClick={addEditItem}>
                  <Icons.Plus /> Adicionar óculos
                </button>
              </div>

              <div className="edit-items-stack">
                {editForm.itens.map((item, index) => (
                  <div key={index} className="edit-item-card">
                    <div className="edit-item-head">
                      <strong>Óculos {index + 1}</strong>
                      <button type="button" onClick={() => removeEditItem(index)}>
                        <Icons.Trash /> Remover
                      </button>
                    </div>

                    <div className="edit-grid-3">
                      <label>
                        Paciente
                        <input value={item.paciente.nome} onChange={(e) => updateEditItem(index, 'paciente.nome', e.target.value)} />
                      </label>
                      <label>
                        Lente
                        <input value={item.produto.lente} onChange={(e) => updateEditItem(index, 'produto.lente', e.target.value)} />
                      </label>
                      <label>
                        Armação
                        <input value={item.produto.armacao} onChange={(e) => updateEditItem(index, 'produto.armacao', e.target.value)} />
                      </label>
                      <label>
                        CV
                        <input value={item.produto.numeroCV} onChange={(e) => updateEditItem(index, 'produto.numeroCV', e.target.value)} />
                      </label>
                      <label>
                        Origem
                        <select value={item.produto.origemArmacao} onChange={(e) => updateEditItem(index, 'produto.origemArmacao', e.target.value)}>
                          <option>Ótica</option>
                          <option>Própria do Cliente</option>
                        </select>
                      </label>
                      <label>
                        Envio
                        <select value={item.produto.envioArmacao} onChange={(e) => updateEditItem(index, 'produto.envioArmacao', e.target.value)}>
                          <option>Vai no malote</option>
                          <option>Fica na loja (Já na óptica)</option>
                          <option>Não enviar para lab (Apenas Lente)</option>
                        </select>
                      </label>
                      <label>
                        Formato
                        <input value={item.produto.formatoArmacao} onChange={(e) => updateEditItem(index, 'produto.formatoArmacao', e.target.value)} />
                      </label>
                      <label>
                        Quantidade
                        <input value={item.quantidade} onChange={(e) => updateEditItem(index, 'quantidade', e.target.value)} />
                      </label>
                      <label>
                        Valor item
                        <input value={item.valorTotal} onChange={(e) => updateEditItem(index, 'valorTotal', e.target.value)} />
                      </label>
                    </div>

                    <div className="rx-edit-grid">
                      <span>OD</span>
                      <input placeholder="ESF" value={item.receita.od.esf} onChange={(e) => updateEditItem(index, 'receita.od.esf', e.target.value)} />
                      <input placeholder="CIL" value={item.receita.od.cil} onChange={(e) => updateEditItem(index, 'receita.od.cil', e.target.value)} />
                      <input placeholder="Eixo" value={item.receita.od.eixo} onChange={(e) => updateEditItem(index, 'receita.od.eixo', e.target.value)} />
                      <input placeholder="DNP" value={item.receita.od.dnp} onChange={(e) => updateEditItem(index, 'receita.od.dnp', e.target.value)} />
                      <input placeholder="ALT" value={item.receita.od.aco} onChange={(e) => updateEditItem(index, 'receita.od.aco', e.target.value)} />

                      <span>OE</span>
                      <input placeholder="ESF" value={item.receita.oe.esf} onChange={(e) => updateEditItem(index, 'receita.oe.esf', e.target.value)} />
                      <input placeholder="CIL" value={item.receita.oe.cil} onChange={(e) => updateEditItem(index, 'receita.oe.cil', e.target.value)} />
                      <input placeholder="Eixo" value={item.receita.oe.eixo} onChange={(e) => updateEditItem(index, 'receita.oe.eixo', e.target.value)} />
                      <input placeholder="DNP" value={item.receita.oe.dnp} onChange={(e) => updateEditItem(index, 'receita.oe.dnp', e.target.value)} />
                      <input placeholder="ALT" value={item.receita.oe.aco} onChange={(e) => updateEditItem(index, 'receita.oe.aco', e.target.value)} />

                      <span>ADD</span>
                      <input className="rx-add" placeholder="Adição" value={item.receita.adicao} onChange={(e) => updateEditItem(index, 'receita.adicao', e.target.value)} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flat-panel full">
              <div className="panel-title-line">
                <h3>Pagamentos</h3>
                <button type="button" className="small-clean-btn" onClick={addEditPagamento}>
                  <Icons.Plus /> Adicionar pagamento
                </button>
              </div>

              <div className="edit-payments-stack">
                {editForm.pagamentos.length ? (
                  editForm.pagamentos.map((pagamento, index) => (
                    <div key={index} className="edit-payment-row">
                      <select value={pagamento.metodo} onChange={(e) => updateEditPagamento(index, 'metodo', e.target.value)}>
                        <option>Dinheiro</option>
                        <option>PIX</option>
                        <option>Débito</option>
                        <option>Crédito</option>
                      </select>
                      <input value={pagamento.valor} onChange={(e) => updateEditPagamento(index, 'valor', e.target.value)} placeholder="Valor" />
                      <input value={pagamento.parcelas} onChange={(e) => updateEditPagamento(index, 'parcelas', e.target.value)} placeholder="Parcelas" />
                      <button type="button" onClick={() => removeEditPagamento(index)}>
                        <Icons.Trash />
                      </button>
                    </div>
                  ))
                ) : (
                  <small className="muted-note">Nenhum pagamento registrado.</small>
                )}
              </div>
            </div>

            <div className="flat-panel full">
              <h3>Fiscal manual / legado</h3>
              <div className="edit-grid-3">
                <label>
                  PDF NFC-e
                  <input value={editForm.nfceUrl} onChange={(e) => updateEditField('nfceUrl', e.target.value)} />
                </label>
                <label>
                  XML NFC-e
                  <input value={editForm.nfceXmlUrl} onChange={(e) => updateEditField('nfceXmlUrl', e.target.value)} />
                </label>
                <label>
                  Chave NF-e
                  <input value={editForm.chaveNfe} onChange={(e) => updateEditField('chaveNfe', e.target.value)} />
                </label>
              </div>
            </div>

            <div className="edit-footer-sticky">
              <button type="button" className="secondary-clean-btn" onClick={() => setEditForm(montarEditFormOS(os, empresas, empresaIdAtual))}>
                Reverter
              </button>
              <button type="button" className="primary-clean-btn" onClick={handleSalvarEdicao} disabled={saving}>
                <Icons.Save />
                {saving ? 'Salvando...' : 'Salvar edição'}
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {fotoZoom ? (
        <div className="zoom-clean" onClick={() => setFotoZoom(null)}>
          <button type="button" onClick={() => setFotoZoom(null)}>
            <Icons.Close />
          </button>

          <ProtectedImage
            src={fotoZoom}
            alt="Foto ampliada"
            className="zoom-clean-img"
            fallbackText="Foto"
          />
        </div>
      ) : null}
    </div>
  );
}

function criarItemEditavel(item = {}) {
  return {
    paciente: {
      nome: safeRenderName(item.paciente) || ''
    },
    receita: {
      od: {
        esf: item.receita?.od?.esf || '',
        cil: item.receita?.od?.cil || '',
        eixo: item.receita?.od?.eixo || '',
        dnp: item.receita?.od?.dnp || '',
        aco: item.receita?.od?.aco || ''
      },
      oe: {
        esf: item.receita?.oe?.esf || '',
        cil: item.receita?.oe?.cil || '',
        eixo: item.receita?.oe?.eixo || '',
        dnp: item.receita?.oe?.dnp || '',
        aco: item.receita?.oe?.aco || ''
      },
      adicao: item.receita?.adicao || ''
    },
    produto: {
      lente: item.produto?.lente || '',
      armacao: item.produto?.armacao || '',
      numeroCV: item.produto?.numeroCV || item.produto?.cv || item.produto?.codigoCV || '',
      medidasArmacao: {
        aro: item.produto?.medidasArmacao?.aro || '',
        ponte: item.produto?.medidasArmacao?.ponte || '',
        altura: item.produto?.medidasArmacao?.altura || '',
        diagonalMaior: item.produto?.medidasArmacao?.diagonalMaior || ''
      },
      origemArmacao: item.produto?.origemArmacao || 'Ótica',
      envioArmacao: item.produto?.envioArmacao || 'Vai no malote',
      formatoArmacao: item.produto?.formatoArmacao || '',
      ncm: item.produto?.ncm || '',
      cfop: item.produto?.cfop || ''
    },
    quantidade: item.quantidade || 1,
    valorUnitario: item.valorUnitario || '',
    valorTotal: item.valorTotal || ''
  };
}

function montarEditFormOS(os = {}, empresas = [], empresaIdAtual = '') {
  const empresa = encontrarEmpresaDaOS(os, empresas);
  const itens = getOsItems(os);

  return {
    empresaId: normalizarId(empresa?._id || empresa?.id || getEmpresaIdOS(os) || empresaIdAtual || ''),
    loja: getEmpresaCodigo(empresa || {}) || os.loja || '',
    vendedor: os.vendedor || '',
    whatsapp: os.whatsapp || '',
    dataCompra: toInputDate(os.dataCompra),
    dataEntrega: toInputDate(os.dataEntrega),
    valorTotal: os.valorTotal ?? '',
    cliente: {
      nome: safeRenderName(os.cliente) || '',
      cpf: os.cliente?.cpf || '',
      documento: os.cliente?.documento || '',
      email: os.cliente?.email || '',
      dataNascimento: toInputDate(os.cliente?.dataNascimento)
    },
    endereco: {
      cep: os.endereco?.cep || '',
      logradouro: os.endereco?.logradouro || '',
      numero: os.endereco?.numero || '',
      complemento: os.endereco?.complemento || '',
      bairro: os.endereco?.bairro || '',
      cidade: os.endereco?.cidade || '',
      uf: os.endereco?.uf || ''
    },
    itens: itens.length ? itens.map(criarItemEditavel) : [criarItemEditavel()],
    pagamentos: Array.isArray(os.pagamentos)
      ? os.pagamentos.map((pagamento) => ({
          metodo: pagamento.metodo || 'PIX',
          valor: pagamento.valor ?? '',
          parcelas: pagamento.parcelas || 1,
          data: pagamento.data || new Date().toISOString()
        }))
      : [],
    nfceUrl: os.nfceUrl || os.notaFiscal?.pdfUrl || '',
    nfceXmlUrl: os.nfceXmlUrl || os.notaFiscal?.xmlUrl || '',
    chaveNfe: os.chaveNfe || os.notaFiscal?.chave || ''
  };
}

/* =============================================================
   CSS
============================================================= */

function GlobalStyles() {
  return (
    <style>{`
      :root {
        --lumix-blue: #1a73e8;
        --lumix-bg: #f8fafd;
        --lumix-surface: #ffffff;
        --lumix-surface-2: #f1f3f4;
        --lumix-text: #202124;
        --lumix-muted: #5f6368;
        --lumix-soft: #80868b;
        --lumix-border: #dadce0;
        --lumix-border-soft: #edf0f3;
        --lumix-green: #188038;
        --lumix-red: #d93025;
        --lumix-font: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, Arial, sans-serif;
      }

      * {
        box-sizing: border-box;
      }

      .lumix-os-page {
        min-height: 100vh;
        padding: 18px;
        color: var(--lumix-text);
        background: var(--lumix-bg);
        font-family: var(--lumix-font);
        -webkit-font-smoothing: antialiased;
        text-rendering: geometricPrecision;
      }

      .lumix-top {
        margin-bottom: 12px;
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: 16px;
      }

      .lumix-top-left {
        display: flex;
        align-items: flex-start;
        gap: 12px;
      }

      .plain-btn {
        height: 32px;
        padding: 0 10px;
        border: 1px solid var(--lumix-border);
        border-radius: 999px;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        color: var(--lumix-muted);
        background: #fff;
        font-size: 12px;
        font-weight: 500;
        cursor: pointer;
      }

      .plain-btn:hover,
      .plain-icon-btn:hover {
        background: var(--lumix-surface-2);
        color: var(--lumix-text);
      }

      .plain-icon-btn {
        width: 34px;
        height: 34px;
        border: 1px solid var(--lumix-border);
        border-radius: 999px;
        display: grid;
        place-items: center;
        color: var(--lumix-muted);
        background: #fff;
        cursor: pointer;
        flex-shrink: 0;
      }

      .kicker {
        display: block;
        margin-bottom: 2px;
        color: var(--lumix-muted);
        font-size: 11px;
        font-weight: 600;
        letter-spacing: .04em;
        text-transform: uppercase;
      }

      .lumix-top h1 {
        margin: 0;
        color: var(--lumix-text);
        font-size: 26px;
        line-height: 1.1;
        font-weight: 600;
        letter-spacing: -.035em;
      }

      .lumix-top p {
        margin: 4px 0 0;
        color: var(--lumix-muted);
        font-size: 13px;
      }

      .top-summary {
        display: flex;
        overflow: hidden;
        border: 1px solid var(--lumix-border);
        border-radius: 12px;
        background: #fff;
      }

      .top-summary div {
        min-width: 104px;
        padding: 9px 12px;
        border-right: 1px solid var(--lumix-border-soft);
        display: grid;
        gap: 2px;
      }

      .top-summary div:last-child {
        border-right: 0;
      }

      .top-summary span {
        color: var(--lumix-muted);
        font-size: 11px;
        font-weight: 600;
      }

      .top-summary strong {
        color: var(--lumix-text);
        font-size: 16px;
        line-height: 1.15;
        font-weight: 600;
      }

      .stats-strip {
        margin-bottom: 10px;
        border: 1px solid var(--lumix-border);
        border-radius: 13px;
        display: grid;
        grid-template-columns: repeat(6, minmax(0, 1fr));
        overflow: hidden;
        background: #fff;
      }

      .stat-cell {
        min-height: 54px;
        padding: 9px 10px;
        border: 0;
        border-right: 1px solid var(--lumix-border-soft);
        display: grid;
        grid-template-columns: auto 1fr auto;
        align-items: center;
        gap: 7px;
        color: var(--lumix-text);
        background: #fff;
        cursor: pointer;
        font-family: inherit;
        text-align: left;
      }

      .stat-cell:last-child {
        border-right: 0;
      }

      .stat-cell:hover {
        background: #fbfcfe;
      }

      .stat-cell.active {
        background: var(--cell-bg);
      }

      .stat-cell-dot {
        width: 7px;
        height: 7px;
        border-radius: 99px;
        background: var(--cell-dot);
      }

      .stat-cell-text {
        min-width: 0;
        overflow: hidden;
        color: var(--lumix-muted);
        font-size: 12px;
        font-weight: 600;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .stat-cell strong {
        color: var(--lumix-text);
        font-size: 17px;
        font-weight: 650;
      }

      .filters-line {
        margin-bottom: 10px;
        padding: 9px;
        border: 1px solid var(--lumix-border);
        border-radius: 13px;
        display: grid;
        grid-template-columns: minmax(220px, 1fr) minmax(170px, 230px) 138px 138px auto auto;
        gap: 8px;
        background: #fff;
      }

      .search-box {
        height: 34px;
        padding: 0 10px;
        border: 1px solid var(--lumix-border);
        border-radius: 999px;
        display: flex;
        align-items: center;
        gap: 8px;
        color: var(--lumix-muted);
        background: #fff;
      }

      .search-box:focus-within,
      .filters-line select:focus,
      .filters-line input:focus,
      .status-form-grid select:focus,
      .status-form-grid input:focus {
        border-color: var(--lumix-blue);
        box-shadow: 0 0 0 3px rgba(26, 115, 232, .10);
      }

      .search-box input {
        width: 100%;
        border: 0;
        outline: 0;
        color: var(--lumix-text);
        background: transparent;
        font-size: 12.5px;
        font-family: inherit;
      }

      .filters-line select,
      .filters-line > input {
        height: 34px;
        padding: 0 10px;
        border: 1px solid var(--lumix-border);
        border-radius: 999px;
        outline: 0;
        color: var(--lumix-text);
        background: #fff;
        font-size: 12px;
        font-family: inherit;
      }

      .content-grid {
        min-height: calc(100vh - 190px);
        display: grid;
        grid-template-columns: minmax(360px, .88fr) minmax(420px, 1.12fr);
        gap: 10px;
      }

      .list-pane,
      .detail-pane {
        min-height: 520px;
        border: 1px solid var(--lumix-border);
        border-radius: 14px;
        overflow: hidden;
        background: #fff;
      }

      .pane-head {
        height: 40px;
        padding: 0 12px;
        border-bottom: 1px solid var(--lumix-border-soft);
        display: flex;
        align-items: center;
        justify-content: space-between;
        color: var(--lumix-muted);
        font-size: 12px;
        font-weight: 600;
        background: #fff;
      }

      .pane-head small {
        color: var(--lumix-soft);
        font-size: 11px;
        font-weight: 500;
      }

      .os-list {
        max-height: calc(100vh - 234px);
        overflow: auto;
      }

      .os-list::-webkit-scrollbar,
      .detail-body::-webkit-scrollbar {
        width: 7px;
        height: 7px;
      }

      .os-list::-webkit-scrollbar-track,
      .detail-body::-webkit-scrollbar-track {
        background: transparent;
      }

      .os-list::-webkit-scrollbar-thumb,
      .detail-body::-webkit-scrollbar-thumb {
        border-radius: 999px;
        background: rgba(95, 99, 104, .28);
      }

      .os-row {
        width: 100%;
        min-height: 96px;
        padding: 10px 12px;
        border: 0;
        border-bottom: 1px solid var(--lumix-border-soft);
        display: grid;
        grid-template-columns: 48px minmax(0, 1fr) 134px;
        gap: 10px;
        align-items: center;
        background: #fff;
        color: var(--lumix-text);
        font-family: inherit;
        text-align: left;
        cursor: pointer;
      }

      .os-row:hover {
        background: #fbfcfe;
      }

      .os-row.active {
        background: #f8fbff;
        box-shadow: inset 3px 0 0 var(--lumix-blue);
      }

      .row-photo {
        width: 48px;
        height: 48px;
        border: 1px solid var(--lumix-border);
        border-radius: 12px;
        overflow: hidden;
        background: #f1f3f4;
      }

      .row-photo-img,
      .protected-image-fallback.row-photo-img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .protected-image-fallback {
        display: grid;
        place-items: center;
        color: var(--lumix-muted);
        background: #f1f3f4;
        font-size: 12px;
        font-weight: 650;
      }

      .row-main {
        min-width: 0;
        display: grid;
        gap: 3px;
      }

      .row-title-line,
      .row-sub-line,
      .row-product-line,
      .row-bottom-line {
        min-width: 0;
        display: flex;
        align-items: center;
        gap: 7px;
      }

      .row-title-line strong {
        min-width: 0;
        overflow: hidden;
        color: var(--lumix-text);
        font-size: 14px;
        font-weight: 650;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .row-title-line span {
        color: var(--lumix-muted);
        font-size: 11px;
        font-weight: 600;
        white-space: nowrap;
      }

      .row-sub-line span,
      .row-product-line span,
      .row-product-line em,
      .row-bottom-line span {
        min-width: 0;
        overflow: hidden;
        color: var(--lumix-muted);
        font-size: 11.5px;
        font-style: normal;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .row-sub-line i {
        padding: 1px 6px;
        border: 1px solid var(--lumix-border);
        border-radius: 999px;
        color: var(--lumix-text);
        background: #fff;
        font-size: 10px;
        font-style: normal;
        font-weight: 650;
        white-space: nowrap;
      }

      .row-product-line span {
        color: var(--lumix-text);
        font-weight: 550;
      }

      .row-side {
        display: grid;
        justify-items: end;
        gap: 7px;
      }

      .status-badge {
        max-width: 100%;
        padding: 3px 7px;
        border: 1px solid var(--status-border);
        border-radius: 999px;
        display: inline-flex;
        align-items: center;
        gap: 5px;
        color: var(--status-text);
        background: var(--status-bg);
        font-size: 10px;
        font-weight: 750;
        letter-spacing: .025em;
        text-transform: uppercase;
        white-space: nowrap;
      }

      .status-badge i {
        width: 6px;
        height: 6px;
        border-radius: 999px;
        background: var(--status-dot);
      }

      .row-money {
        display: grid;
        justify-items: end;
        gap: 1px;
      }

      .row-money strong {
        color: var(--lumix-text);
        font-size: 12px;
        font-weight: 650;
      }

      .row-money span {
        color: var(--lumix-muted);
        font-size: 10.5px;
      }

      .mini-paybar {
        width: 88px;
        height: 4px;
        overflow: hidden;
        border-radius: 99px;
        background: #edf0f3;
      }

      .mini-paybar i {
        display: block;
        height: 100%;
        border-radius: inherit;
        background: var(--lumix-blue);
      }

      .empty-panel,
      .detail-placeholder,
      .detail-loading {
        min-height: 260px;
        padding: 24px;
        display: grid;
        place-items: center;
        align-content: center;
        gap: 8px;
        color: var(--lumix-muted);
        text-align: center;
        font-size: 13px;
      }

      .empty-panel button {
        border: 0;
        color: var(--lumix-blue);
        background: transparent;
        font: inherit;
        font-weight: 600;
        cursor: pointer;
      }

      .thin-loader {
        width: 180px;
        height: 3px;
        border-radius: 999px;
        overflow: hidden;
        background: #e8eaed;
        position: relative;
      }

      .thin-loader::after {
        content: "";
        position: absolute;
        inset: 0;
        width: 40%;
        border-radius: inherit;
        background: var(--lumix-blue);
        animation: loader-move 1.1s linear infinite;
      }

      @keyframes loader-move {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(260%); }
      }

      .detail-shell {
        min-height: 100%;
        display: flex;
        flex-direction: column;
        background: #fff;
      }

      .detail-top {
        padding: 14px;
        border-bottom: 1px solid var(--lumix-border-soft);
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 10px;
      }

      .detail-profile {
        min-width: 0;
        display: flex;
        align-items: center;
        gap: 11px;
      }

      .detail-avatar,
      .protected-image-fallback.detail-avatar {
        width: 58px;
        height: 58px;
        border: 1px solid var(--lumix-border);
        border-radius: 15px;
        object-fit: cover;
        overflow: hidden;
        flex-shrink: 0;
      }

      .detail-kicker {
        display: block;
        margin-bottom: 3px;
        color: var(--lumix-muted);
        font-size: 11px;
        font-weight: 650;
        letter-spacing: .04em;
        text-transform: uppercase;
      }

      .detail-profile h2 {
        margin: 0;
        color: var(--lumix-text);
        font-size: 18px;
        line-height: 1.18;
        font-weight: 650;
        letter-spacing: -.025em;
      }

      .detail-profile p {
        margin: 3px 0 0;
        color: var(--lumix-muted);
        font-size: 12px;
      }

      .detail-meta-line {
        min-height: 38px;
        padding: 0 14px;
        border-bottom: 1px solid var(--lumix-border-soft);
        display: flex;
        align-items: center;
        gap: 10px;
        color: var(--lumix-muted);
        font-size: 11.5px;
        overflow-x: auto;
        white-space: nowrap;
      }

      .detail-tabs {
        min-height: 39px;
        padding: 0 8px;
        border-bottom: 1px solid var(--lumix-border-soft);
        display: flex;
        gap: 2px;
        background: #fff;
        overflow-x: auto;
      }

      .detail-tabs button {
        padding: 0 10px;
        border: 0;
        border-bottom: 2px solid transparent;
        color: var(--lumix-muted);
        background: transparent;
        font-size: 12px;
        font-weight: 650;
        font-family: inherit;
        cursor: pointer;
        white-space: nowrap;
      }

      .detail-tabs button:hover {
        color: var(--lumix-text);
        background: #f8fafd;
      }

      .detail-tabs button.active {
        color: var(--lumix-blue);
        border-bottom-color: var(--lumix-blue);
      }

      .detail-body {
        flex: 1;
        min-height: 0;
        padding: 12px;
        overflow: auto;
        background: #fff;
      }

      .detail-section-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 10px;
      }

      .flat-panel {
        padding: 12px;
        border: 1px solid var(--lumix-border);
        border-radius: 13px;
        background: #fff;
      }

      .flat-panel.full {
        grid-column: 1 / -1;
      }

      .flat-panel h3 {
        margin: 0 0 10px;
        color: var(--lumix-text);
        font-size: 13px;
        font-weight: 700;
        letter-spacing: -.01em;
      }

      .field-line {
        padding: 7px 0;
        border-bottom: 1px solid var(--lumix-border-soft);
        display: grid;
        grid-template-columns: 116px minmax(0, 1fr);
        gap: 8px;
      }

      .field-line:last-child {
        border-bottom: 0;
      }

      .field-line span {
        color: var(--lumix-muted);
        font-size: 11px;
        font-weight: 600;
      }

      .field-line strong {
        min-width: 0;
        color: var(--lumix-text);
        font-size: 12.5px;
        font-weight: 600;
        word-break: break-word;
      }

      .payment-list-clean {
        margin-top: 10px;
        border-top: 1px solid var(--lumix-border-soft);
      }

      .payment-list-clean div {
        min-height: 30px;
        border-bottom: 1px solid var(--lumix-border-soft);
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        color: var(--lumix-muted);
        font-size: 12px;
      }

      .payment-list-clean small {
        display: block;
        padding-top: 10px;
        color: var(--lumix-muted);
        font-size: 12px;
      }

      .history-clean {
        max-height: 220px;
        overflow: auto;
      }

      .history-clean div {
        padding: 8px 0;
        border-bottom: 1px solid var(--lumix-border-soft);
      }

      .history-clean span {
        display: block;
        margin-bottom: 3px;
        color: var(--lumix-soft);
        font-size: 10.5px;
        font-weight: 600;
      }

      .history-clean p {
        margin: 0;
        color: var(--lumix-text);
        font-size: 12.5px;
        line-height: 1.4;
      }

      .items-list-clean {
        display: grid;
        gap: 10px;
      }

      .item-clean-card {
        border: 1px solid var(--lumix-border);
        border-radius: 13px;
        overflow: hidden;
        background: #fff;
      }

      .item-clean-head {
        padding: 10px 12px;
        border-bottom: 1px solid var(--lumix-border-soft);
        display: flex;
        justify-content: space-between;
        gap: 10px;
        background: #f8fafd;
      }

      .item-clean-head strong {
        color: var(--lumix-text);
        font-size: 13px;
      }

      .item-clean-head span {
        color: var(--lumix-muted);
        font-size: 12px;
      }

      .item-clean-grid {
        padding: 8px 12px;
        display: grid;
        grid-template-columns: 1fr 1fr;
        column-gap: 14px;
      }

      .rx-clean {
        padding: 10px 12px 12px;
        border-top: 1px solid var(--lumix-border-soft);
      }

      .rx-clean table {
        width: 100%;
        border-collapse: collapse;
        overflow: hidden;
        border: 1px solid var(--lumix-border);
        border-radius: 10px;
        font-size: 12px;
      }

      .rx-clean th,
      .rx-clean td {
        padding: 7px 8px;
        border-bottom: 1px solid var(--lumix-border-soft);
        border-right: 1px solid var(--lumix-border-soft);
        text-align: center;
      }

      .rx-clean th {
        color: var(--lumix-muted);
        background: #f8fafd;
        font-size: 10.5px;
        text-transform: uppercase;
      }

      .rx-clean td:first-child {
        color: var(--lumix-text);
        font-weight: 700;
      }

      .rx-clean span {
        display: inline-block;
        margin-top: 9px;
        color: var(--lumix-muted);
        font-size: 12px;
        font-weight: 600;
      }

      .photos-section {
        display: grid;
        gap: 10px;
      }

      .photo-toolbar {
        min-height: 40px;
        display: flex;
        align-items: center;
        gap: 10px;
      }

      .upload-clean-btn {
        height: 34px;
        padding: 0 12px;
        border: 1px solid var(--lumix-border);
        border-radius: 999px;
        display: inline-flex;
        align-items: center;
        gap: 7px;
        color: var(--lumix-text);
        background: #fff;
        font-size: 12px;
        font-weight: 650;
        cursor: pointer;
      }

      .upload-clean-btn:hover {
        background: #f8fafd;
      }

      .upload-clean-btn input {
        display: none;
      }

      .upload-progress-line {
        flex: 1;
        height: 4px;
        overflow: hidden;
        border-radius: 999px;
        background: #e8eaed;
      }

      .upload-progress-line i {
        display: block;
        height: 100%;
        border-radius: inherit;
        background: var(--lumix-blue);
      }

      .photo-grid-clean {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(118px, 1fr));
        gap: 8px;
      }

      .photo-clean-card {
        position: relative;
        aspect-ratio: 1 / 1;
        border: 1px solid var(--lumix-border);
        border-radius: 13px;
        overflow: hidden;
        background: #f1f3f4;
      }

      .photo-clean-img,
      .protected-image-fallback.photo-clean-img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        cursor: zoom-in;
      }

      .photo-remove {
        position: absolute;
        top: 6px;
        right: 6px;
        width: 28px;
        height: 28px;
        border: 1px solid rgba(255,255,255,.75);
        border-radius: 999px;
        display: grid;
        place-items: center;
        color: #fff;
        background: rgba(0,0,0,.42);
        cursor: pointer;
      }

      .status-form-grid {
        display: grid;
        gap: 10px;
      }

      .status-form-grid label {
        display: grid;
        gap: 5px;
        color: var(--lumix-muted);
        font-size: 11px;
        font-weight: 650;
      }

      .status-form-grid select,
      .status-form-grid input {
        height: 36px;
        padding: 0 10px;
        border: 1px solid var(--lumix-border);
        border-radius: 10px;
        outline: 0;
        color: var(--lumix-text);
        background: #fff;
        font-size: 12.5px;
        font-family: inherit;
      }

      .primary-clean-btn {
        margin-top: 12px;
        height: 36px;
        padding: 0 14px;
        border: 1px solid #1557b0;
        border-radius: 999px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 7px;
        color: #fff;
        background: var(--lumix-blue);
        font-size: 12.5px;
        font-weight: 700;
        font-family: inherit;
        cursor: pointer;
      }

      .primary-clean-btn:disabled {
        opacity: .65;
        cursor: not-allowed;
      }

      .label-with-status {
        display: inline-flex;
        align-items: center;
        gap: 7px;
      }

      .label-with-status small {
        color: var(--lumix-blue);
        font-size: 10px;
        font-weight: 650;
      }

      .zoom-clean {
        position: fixed;
        inset: 0;
        z-index: 9999;
        padding: 28px;
        display: grid;
        place-items: center;
        background: rgba(32, 33, 36, .74);
      }

      .zoom-clean button {
        position: fixed;
        top: 16px;
        right: 16px;
        width: 36px;
        height: 36px;
        border: 1px solid rgba(255,255,255,.35);
        border-radius: 999px;
        display: grid;
        place-items: center;
        color: #fff;
        background: rgba(0,0,0,.22);
        cursor: pointer;
      }

      .zoom-clean-img,
      .protected-image-fallback.zoom-clean-img {
        max-width: min(980px, 96vw);
        max-height: 88vh;
        border-radius: 14px;
        object-fit: contain;
        background: #fff;
      }


      .panel-title-line {
        margin-bottom: 10px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
      }

      .panel-title-line h3 {
        margin: 0;
      }

      .panel-title-line span {
        color: var(--lumix-muted);
        font-size: 11.5px;
        font-weight: 500;
      }

      .observation-panel {
        background: #fbfcfe;
      }

      .quick-note-row {
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 8px;
      }

      .quick-note-row input {
        height: 36px;
        padding: 0 10px;
        border: 1px solid var(--lumix-border);
        border-radius: 10px;
        outline: 0;
        color: var(--lumix-text);
        background: #fff;
        font-size: 12.5px;
        font-family: inherit;
      }

      .quick-note-row input:focus {
        border-color: var(--lumix-blue);
        box-shadow: 0 0 0 3px rgba(26, 115, 232, .10);
      }

      .quick-note-row button,
      .small-clean-btn,
      .secondary-clean-btn {
        height: 34px;
        padding: 0 12px;
        border: 1px solid var(--lumix-border);
        border-radius: 999px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        color: var(--lumix-text);
        background: #fff;
        font-size: 12px;
        font-weight: 650;
        font-family: inherit;
        cursor: pointer;
        white-space: nowrap;
      }

      .quick-note-row button:hover,
      .small-clean-btn:hover,
      .secondary-clean-btn:hover {
        background: #f8fafd;
      }

      .status-panel-clean {
        max-width: 620px;
      }

      .ready-notice {
        padding: 10px;
        border: 1px solid #bbf7d0;
        border-radius: 12px;
        background: #f0fdf4;
      }

      .ready-notice strong {
        display: block;
        margin-bottom: 3px;
        color: #15803d;
        font-size: 12.5px;
      }

      .ready-notice p {
        margin: 0 0 8px;
        color: #166534;
        font-size: 12px;
        line-height: 1.4;
      }

      .checkbox-clean {
        display: inline-flex !important;
        grid-template-columns: none !important;
        align-items: center;
        gap: 7px !important;
        color: #166534 !important;
        font-size: 12px !important;
        font-weight: 650 !important;
      }

      .checkbox-clean input {
        width: 14px;
        height: 14px;
        margin: 0;
      }

      .edit-clean-layout {
        display: grid;
        gap: 10px;
      }

      .edit-grid-3 {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 9px;
      }

      .edit-grid-3 label,
      .edit-item-card label,
      .edit-payment-row label {
        min-width: 0;
        display: grid;
        gap: 5px;
        color: var(--lumix-muted);
        font-size: 11px;
        font-weight: 650;
      }

      .edit-grid-3 input,
      .edit-grid-3 select,
      .edit-item-card input,
      .edit-item-card select,
      .edit-payment-row input,
      .edit-payment-row select,
      .rx-edit-grid input {
        min-width: 0;
        height: 34px;
        padding: 0 9px;
        border: 1px solid var(--lumix-border);
        border-radius: 9px;
        outline: 0;
        color: var(--lumix-text);
        background: #fff;
        font-size: 12px;
        font-family: inherit;
      }

      .edit-grid-3 input:focus,
      .edit-grid-3 select:focus,
      .edit-item-card input:focus,
      .edit-item-card select:focus,
      .edit-payment-row input:focus,
      .edit-payment-row select:focus,
      .rx-edit-grid input:focus {
        border-color: var(--lumix-blue);
        box-shadow: 0 0 0 3px rgba(26, 115, 232, .10);
      }

      .span-2 {
        grid-column: span 2;
      }

      .edit-items-stack,
      .edit-payments-stack {
        display: grid;
        gap: 8px;
      }

      .edit-item-card {
        border: 1px solid var(--lumix-border);
        border-radius: 12px;
        overflow: hidden;
        background: #fff;
      }

      .edit-item-head {
        min-height: 38px;
        padding: 8px 10px;
        border-bottom: 1px solid var(--lumix-border-soft);
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        background: #f8fafd;
      }

      .edit-item-head strong {
        color: var(--lumix-text);
        font-size: 12.5px;
      }

      .edit-item-head button,
      .edit-payment-row button {
        height: 30px;
        padding: 0 9px;
        border: 1px solid var(--lumix-border);
        border-radius: 999px;
        display: inline-flex;
        align-items: center;
        gap: 5px;
        color: var(--lumix-muted);
        background: #fff;
        font-size: 11.5px;
        font-weight: 650;
        cursor: pointer;
      }

      .edit-item-head button:hover,
      .edit-payment-row button:hover {
        color: var(--lumix-red);
        background: #fef2f2;
      }

      .edit-item-card .edit-grid-3 {
        padding: 10px;
      }

      .rx-edit-grid {
        padding: 0 10px 10px;
        display: grid;
        grid-template-columns: 36px repeat(5, minmax(0, 1fr));
        gap: 6px;
        align-items: center;
      }

      .rx-edit-grid span {
        color: var(--lumix-text);
        font-size: 11px;
        font-weight: 750;
        text-align: center;
      }

      .rx-edit-grid .rx-add {
        grid-column: span 2;
      }

      .edit-payment-row {
        display: grid;
        grid-template-columns: 1fr 1fr 90px auto;
        gap: 8px;
        align-items: center;
      }

      .muted-note {
        color: var(--lumix-muted);
        font-size: 12px;
      }

      .edit-footer-sticky {
        position: sticky;
        bottom: -12px;
        z-index: 5;
        padding: 10px 0 0;
        border-top: 1px solid var(--lumix-border-soft);
        display: flex;
        justify-content: flex-end;
        gap: 8px;
        background: #fff;
      }

      @media (max-width: 1100px) {
        .stats-strip {
          grid-template-columns: repeat(3, minmax(0, 1fr));
        }

        .stat-cell:nth-child(3) {
          border-right: 0;
        }

        .filters-line {
          grid-template-columns: 1fr 1fr;
        }

        .content-grid {
          grid-template-columns: 1fr;
        }

        .os-list {
          max-height: none;
        }

        .detail-pane {
          min-height: auto;
        }
      }

      @media (max-width: 680px) {
        .lumix-os-page {
          padding: 10px;
        }

        .lumix-top {
          align-items: stretch;
          flex-direction: column;
        }

        .lumix-top-left {
          flex-direction: column;
          gap: 8px;
        }

        .top-summary {
          width: 100%;
        }

        .top-summary div {
          flex: 1;
        }

        .stats-strip {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        .stat-cell {
          border-bottom: 1px solid var(--lumix-border-soft);
        }

        .filters-line {
          grid-template-columns: 1fr;
        }

        .os-row {
          grid-template-columns: 44px minmax(0, 1fr);
        }

        .row-side {
          grid-column: 1 / -1;
          grid-template-columns: auto 1fr auto;
          align-items: center;
          justify-items: start;
        }

        .row-money {
          justify-items: start;
        }

        .mini-paybar {
          width: 100%;
        }

        .detail-section-grid,
        .item-clean-grid,
        .edit-grid-3 {
          grid-template-columns: 1fr;
        }

        .span-2 {
          grid-column: auto;
        }

        .quick-note-row,
        .edit-payment-row {
          grid-template-columns: 1fr;
        }

        .rx-edit-grid {
          grid-template-columns: 32px repeat(2, minmax(0, 1fr));
        }

        .flat-panel.full {
          grid-column: auto;
        }

        .field-line {
          grid-template-columns: 92px minmax(0, 1fr);
        }
      }

      /* =========================================================
         LUMIX WORKSPACE 2.0
         Uma única superfície operacional. Sem card dentro de card.
      ========================================================= */
      :root {
        --lumix-blue: #2563eb;
        --lumix-blue-strong: #1d4ed8;
        --lumix-blue-soft: #eff6ff;
        --lumix-bg: #ffffff;
        --lumix-surface: #ffffff;
        --lumix-surface-2: #f7f8fa;
        --lumix-text: #111827;
        --lumix-muted: #667085;
        --lumix-soft: #98a2b3;
        --lumix-border: #dfe3e8;
        --lumix-border-soft: #edf0f2;
        --lumix-green: #16803c;
        --lumix-red: #d92d20;
        --lumix-font: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
      }

      html, body {
        background: #fff;
      }

      button, input, select {
        font-family: var(--lumix-font);
      }

      .lumix-os-page {
        min-height: 100vh;
        padding: 0;
        background: #fff;
        color: var(--lumix-text);
      }

      .lumix-top {
        min-height: 76px;
        margin: 0;
        padding: 14px 22px;
        border-bottom: 1px solid var(--lumix-border);
        align-items: center;
        background: #fff;
      }

      .lumix-top-left {
        align-items: center;
        gap: 14px;
      }

      .plain-btn {
        height: 36px;
        padding: 0 12px;
        border: 0;
        border-radius: 8px;
        background: transparent;
        color: var(--lumix-muted);
      }

      .plain-btn:hover {
        background: var(--lumix-surface-2);
        color: var(--lumix-text);
      }

      .kicker {
        margin: 0 0 3px;
        color: var(--lumix-blue);
        font-size: 10px;
        letter-spacing: .08em;
      }

      .lumix-top h1 {
        font-size: 22px;
        font-weight: 720;
        letter-spacing: -.025em;
      }

      .lumix-top p {
        margin-top: 3px;
        font-size: 12px;
      }

      .top-summary {
        border: 0;
        border-radius: 0;
        background: transparent;
      }

      .top-summary div {
        min-width: 118px;
        padding: 4px 14px;
        border-right: 1px solid var(--lumix-border);
      }

      .top-summary span {
        font-size: 10px;
        text-transform: uppercase;
        letter-spacing: .05em;
      }

      .top-summary strong {
        font-size: 15px;
      }

      .stats-strip {
        min-height: 58px;
        margin: 0;
        padding: 0 18px;
        border: 0;
        border-bottom: 1px solid var(--lumix-border);
        border-radius: 0;
        display: flex;
        gap: 4px;
        overflow-x: auto;
        background: #fff;
      }

      .stat-cell {
        position: relative;
        min-width: 138px;
        min-height: 58px;
        padding: 0 12px;
        border: 0;
        display: grid;
        grid-template-columns: auto 1fr auto;
        gap: 8px;
        background: transparent;
      }

      .stat-cell::after {
        content: "";
        position: absolute;
        right: 10px;
        bottom: 0;
        left: 10px;
        height: 3px;
        border-radius: 3px 3px 0 0;
        background: transparent;
      }

      .stat-cell:hover {
        background: #f9fafb;
      }

      .stat-cell.active {
        background: transparent;
      }

      .stat-cell.active::after {
        background: var(--cell-dot, var(--lumix-blue));
      }

      .stat-cell-text {
        color: var(--lumix-muted);
        font-size: 11.5px;
      }

      .stat-cell.active .stat-cell-text,
      .stat-cell.active strong {
        color: var(--lumix-text);
      }

      .stat-cell strong {
        font-size: 16px;
      }

      .filters-line {
        min-height: 58px;
        margin: 0;
        padding: 10px 18px;
        border: 0;
        border-bottom: 1px solid var(--lumix-border);
        border-radius: 0;
        grid-template-columns: minmax(280px, 1fr) minmax(180px, 240px) 138px 138px 36px 36px;
        gap: 8px;
        background: #fafbfc;
      }

      .search-box {
        height: 38px;
        padding: 0 12px;
        border-color: #cfd5dc;
        border-radius: 8px;
        background: #fff;
      }

      .filters-line select,
      .filters-line > input {
        height: 38px;
        border-color: #cfd5dc;
        border-radius: 8px;
        background: #fff;
      }

      .plain-icon-btn {
        width: 36px;
        height: 36px;
        border: 0;
        border-radius: 8px;
        background: transparent;
      }

      .plain-icon-btn:hover {
        background: #eef1f4;
      }

      .content-grid {
        min-height: calc(100vh - 192px);
        gap: 0;
        grid-template-columns: minmax(430px, 42%) minmax(520px, 58%);
        background: #fff;
      }

      .list-pane,
      .detail-pane {
        min-height: calc(100vh - 192px);
        border: 0;
        border-radius: 0;
        background: #fff;
      }

      .list-pane {
        border-right: 1px solid var(--lumix-border);
      }

      .pane-head {
        height: 42px;
        padding: 0 16px;
        background: #fff;
      }

      .pane-head small {
        display: none;
      }

      .os-list {
        max-height: calc(100vh - 234px);
      }

      .os-row {
        position: relative;
        min-height: 88px;
        padding: 11px 14px;
        grid-template-columns: 44px minmax(0, 1fr) 126px;
        gap: 11px;
        border-bottom-color: var(--lumix-border-soft);
      }

      .os-row:hover {
        background: #f8fafc;
      }

      .os-row.active {
        background: var(--lumix-blue-soft);
        box-shadow: none;
      }

      .os-row.active::before {
        content: "";
        position: absolute;
        top: 10px;
        bottom: 10px;
        left: 0;
        width: 3px;
        border-radius: 0 3px 3px 0;
        background: var(--lumix-blue);
      }

      .row-photo {
        width: 44px;
        height: 44px;
        border: 0;
        border-radius: 50%;
      }

      .row-main {
        gap: 2px;
      }

      .row-title-line strong {
        font-size: 13.5px;
        font-weight: 700;
      }

      .row-title-line span,
      .row-sub-line span,
      .row-product-line span,
      .row-product-line em,
      .row-bottom-line span {
        font-size: 11px;
      }

      .row-sub-line i {
        padding: 0;
        border: 0;
        border-radius: 0;
        background: transparent;
        color: var(--lumix-blue);
      }

      .status-badge {
        padding: 2px 0;
        border: 0;
        border-radius: 0;
        background: transparent;
        font-size: 9.5px;
      }

      .row-money strong {
        font-size: 12.5px;
      }

      .mini-paybar {
        height: 3px;
        background: #e3e7eb;
      }

      .detail-shell {
        height: calc(100vh - 192px);
      }

      .detail-top {
        min-height: 72px;
        padding: 12px 18px;
        border-bottom: 0;
      }

      .detail-avatar {
        width: 46px;
        height: 46px;
        border: 0;
        border-radius: 50%;
      }

      .detail-profile h2 {
        font-size: 18px;
        font-weight: 720;
      }

      .detail-meta-line {
        min-height: 34px;
        padding: 0 18px 9px;
        border-bottom: 1px solid var(--lumix-border);
      }

      .detail-tabs {
        min-height: 44px;
        padding: 0 12px;
        gap: 8px;
        background: #fff;
      }

      .detail-tabs button {
        padding: 0 8px;
        font-size: 11.5px;
      }

      .detail-tabs button.active {
        color: var(--lumix-text);
        border-bottom-color: var(--lumix-blue);
      }

      .detail-body {
        padding: 18px;
        background: #fff;
      }

      .detail-section-grid {
        gap: 0 28px;
      }

      .flat-panel {
        padding: 0;
        border: 0;
        border-radius: 0;
        background: transparent;
      }

      .flat-panel + .flat-panel {
        border-left: 1px solid var(--lumix-border-soft);
        padding-left: 28px;
      }

      .flat-panel.full {
        margin-top: 22px;
        padding-top: 18px;
        border-top: 1px solid var(--lumix-border);
        border-left: 0;
        padding-left: 0;
      }

      .flat-panel h3 {
        margin-bottom: 8px;
        font-size: 12px;
        text-transform: uppercase;
        letter-spacing: .045em;
        color: var(--lumix-muted);
      }

      .field-line {
        padding: 8px 0;
        grid-template-columns: 104px minmax(0, 1fr);
      }

      .field-line span {
        font-size: 11px;
      }

      .field-line strong {
        font-size: 12.5px;
      }

      .observation-panel {
        background: transparent;
      }

      .quick-note-row input {
        height: 40px;
        border-radius: 8px;
      }

      .quick-note-row button,
      .small-clean-btn,
      .secondary-clean-btn,
      .upload-clean-btn {
        height: 38px;
        border-radius: 8px;
      }

      .primary-clean-btn {
        height: 40px;
        border: 0;
        border-radius: 8px;
        background: var(--lumix-blue);
      }

      .primary-clean-btn:hover {
        background: var(--lumix-blue-strong);
      }

      .item-clean-card,
      .edit-item-card {
        border: 0;
        border-radius: 0;
        border-bottom: 1px solid var(--lumix-border);
      }

      .item-clean-head,
      .edit-item-head {
        padding: 10px 0;
        background: transparent;
      }

      .item-clean-grid,
      .edit-item-card .edit-grid-3 {
        padding: 10px 0;
      }

      .rx-clean {
        padding: 12px 0 18px;
      }

      .rx-clean table {
        border-radius: 0;
      }

      .photo-clean-card {
        border-radius: 8px;
      }

      .ready-notice {
        border: 0;
        border-left: 3px solid var(--lumix-green);
        border-radius: 0;
      }

      .edit-clean-layout {
        gap: 24px;
      }

      .edit-grid-3 input,
      .edit-grid-3 select,
      .edit-item-card input,
      .edit-item-card select,
      .edit-payment-row input,
      .edit-payment-row select,
      .rx-edit-grid input,
      .status-form-grid select,
      .status-form-grid input {
        height: 38px;
        border-radius: 8px;
      }

      .edit-footer-sticky {
        bottom: -18px;
        margin: 18px -18px -18px;
        padding: 12px 18px;
        box-shadow: 0 -8px 20px rgba(16, 24, 40, .04);
      }

      .detail-placeholder {
        min-height: calc(100vh - 192px);
        background:
          linear-gradient(#fff, #fff) padding-box,
          repeating-linear-gradient(135deg, transparent 0 12px, rgba(37,99,235,.025) 12px 24px);
      }

      @media (max-width: 1100px) {
        .stats-strip {
          display: flex;
          grid-template-columns: none;
        }

        .stat-cell:nth-child(3) {
          border-right: 0;
        }

        .filters-line {
          grid-template-columns: minmax(220px, 1fr) minmax(180px, .7fr) 138px 138px 36px 36px;
          overflow-x: auto;
        }

        .content-grid {
          grid-template-columns: minmax(360px, 44%) minmax(480px, 56%);
          overflow-x: auto;
        }
      }

      @media (max-width: 760px) {
        .lumix-top {
          min-height: auto;
          padding: 12px 14px;
          align-items: stretch;
        }

        .lumix-top-left {
          flex-direction: row;
          align-items: flex-start;
        }

        .lumix-top p {
          display: none;
        }

        .top-summary {
          padding-left: 42px;
        }

        .top-summary div {
          min-width: 0;
          flex: 1;
          padding: 5px 12px 0 0;
        }

        .stats-strip {
          padding: 0 8px;
        }

        .stat-cell {
          min-width: 124px;
        }

        .filters-line {
          padding: 9px 10px;
          display: flex;
          overflow-x: auto;
        }

        .search-box {
          min-width: 250px;
        }

        .filters-line select {
          min-width: 180px;
        }

        .filters-line > input {
          min-width: 138px;
        }

        .content-grid {
          min-height: auto;
          display: block;
          overflow: visible;
        }

        .list-pane,
        .detail-pane {
          min-height: auto;
          border-right: 0;
        }

        .detail-pane {
          border-top: 1px solid var(--lumix-border);
        }

        .os-list {
          max-height: 55vh;
        }

        .detail-shell {
          height: auto;
          min-height: 65vh;
        }

        .os-row {
          grid-template-columns: 40px minmax(0, 1fr);
        }

        .row-side {
          grid-column: 2;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .mini-paybar {
          display: none;
        }

        .detail-body {
          padding: 14px;
        }

        .detail-section-grid {
          display: block;
        }

        .flat-panel,
        .flat-panel + .flat-panel {
          padding: 0;
          border-left: 0;
        }

        .flat-panel + .flat-panel,
        .flat-panel.full {
          margin-top: 20px;
          padding-top: 18px;
          border-top: 1px solid var(--lumix-border);
        }

        .edit-footer-sticky {
          margin-right: -14px;
          margin-left: -14px;
          padding-right: 14px;
          padding-left: 14px;
        }
      }


      /* =============================================================
         V2 — paginação por lotes e detalhe expandido
      ============================================================= */

      .content-grid.detail-open {
        grid-template-columns: 58px minmax(0, 1fr);
      }

      .content-grid.detail-open .list-pane {
        min-width: 58px;
        border-radius: 10px;
      }

      .list-restore-rail {
        width: 100%;
        height: 100%;
        min-height: 520px;
        padding: 14px 0;
        border: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
        gap: 8px;
        color: #202124;
        background: #fff;
        cursor: pointer;
      }

      .list-restore-rail:hover {
        background: #f8f9fa;
      }

      .list-restore-rail span {
        font-size: 10px;
        font-weight: 700;
        writing-mode: vertical-rl;
        transform: rotate(180deg);
        letter-spacing: .04em;
        text-transform: uppercase;
      }

      .load-more-wrap {
        padding: 12px;
        border-top: 1px solid var(--lumix-border-soft);
        background: #fff;
      }

      .load-more-btn {
        width: 100%;
        height: 38px;
        border: 1px solid #202124;
        border-radius: 8px;
        color: #202124;
        background: #fff;
        font: inherit;
        font-size: 12px;
        font-weight: 700;
        cursor: pointer;
      }

      .load-more-btn:hover:not(:disabled) {
        color: #fff;
        background: #202124;
      }

      .load-more-btn:disabled {
        cursor: wait;
        opacity: .55;
      }

      .content-grid.detail-open .detail-pane {
        width: 100%;
      }

      @media (max-width: 900px) {
        .content-grid.detail-open {
          grid-template-columns: 44px minmax(0, 1fr);
        }

        .list-restore-rail {
          min-height: 420px;
        }
      }

    `}</style>
  );
}
